// src/components/AgreementView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom'; // Remove useSearchParams
import { 
  getAgreementJsonData,
  sendMsg91Otp,
  verifyMsg91Otp,
  generateOtpmsg91,
  acceptRentAgreement,
  rejectRentAgreement,
  verifyOTPAgreement
} from '../apis/services/pgApi';
import ViewAgreementModal from './RentAgreementDynamic';
import '../scss/components/_agreementview.scss';

// src/utils/encryption.js
import * as CryptoJS from 'crypto-js';

const SECRET_KEY = 'cipl';

// Generate SHA256 hash of the secret key (matching backend)
const getSecretKey = () => {
  return CryptoJS.SHA256(SECRET_KEY).toString();
};

// Decrypt agreement parameters
export const decryptAgreementParams = (encryptedParams) => {
  try {
    const parts = encryptedParams.split(':');
    if (parts.length !== 2) {
      console.error('Invalid encrypted format');
      return null;
    }

    const [ivHex, encryptedData] = parts;
    
    const iv = CryptoJS.enc.Hex.parse(ivHex);
    const encrypted = CryptoJS.enc.Hex.parse(encryptedData);
    const key = CryptoJS.enc.Hex.parse(getSecretKey());

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encrypted },
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedText) {
      console.error('Decryption failed');
      return null;
    }

    const params = decryptedText.split('|');
    if (params.length !== 3) {
      console.error('Invalid decrypted format');
      return null;
    }

    return {
      agreementId: parseInt(params[0]),
      tenantId: parseInt(params[1]),
      pgId: parseInt(params[2])
    };
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

const AgreementView = () => { 
  const { encryptedParams } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Decrypted parameters
  const [decryptedParams, setDecryptedParams] = useState(null);
  
  // Agreement consent states
  const [agreementConsent, setAgreementConsent] = useState(false);
  
  // OTP related states
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpId, setOtpId] = useState(null);
  const [otpPurpose, setOtpPurpose] = useState('');
  const [otpMobile, setOtpMobile] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpVerified, setOtpVerified] = useState(false);
  
  // For reject reason
  const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');

  // Action states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(false);

  // NEW: State for PDF upload
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [pdfUploadData, setPdfUploadData] = useState(null);

  // NEW: Agreement Status
  const [agreementStatus, setAgreementStatus] = useState('');

  const [withOrWithoutOtp, setWithOrWithoutOtp] = useState(null);

  const timerRef = useRef(null);

  // Decrypt parameters on component mount
  useEffect(() => {
    if (encryptedParams) {
      const params = decryptAgreementParams(encryptedParams);
      if (params) {
        setDecryptedParams(params);
        console.log('Decrypted params:', params);
      } else {
        setError('Invalid or expired agreement link');
        setLoading(false);
      }
    } else {
      setError('Invalid agreement link');
      setLoading(false);
    }
  }, [encryptedParams]);

  // Get the tenant name from agreement data
  const getTenantName = () => {
    if (!agreementData) return 'Tenant';
    const data = agreementData.agreementjson || agreementData;
    const tenantData = data?.tenant || {};
    return tenantData[0]?.name || tenantData?.Name || 'Tenant';
  };

  // Get tenant mobile from agreement data
  const getTenantMobile = () => {
    if (!agreementData) return '';
    const data = agreementData.agreementjson || agreementData;
    
    if (Array.isArray(data)) {
      const firstItem = data[0] || {};
      const tenantData = firstItem?.tenant || {};
      return tenantData?.mobile || tenantData?.Mobile || '';
    }
    
    const tenantData = data?.tenant || {};
    return tenantData?.mobile || tenantData?.Mobile || '';
  };

  // Get agreement ID
  const getAgreementID = () => {
    return agreementData?.AgreementID || decryptedParams?.agreementId;
  };

  // NEW: Get agreement status display text
  const getAgreementStatusText = (status) => {
    if (status === "TENANT_ACCEPTED") return "Accepted";
    if (status === "REJECTED") return "Rejected";
    if (status === "DRAFT") return "Draft";
    if (status === "Generate") return "Not Generated";
    return "N/A";
  };

  // NEW: Get agreement status color
  const getAgreementStatusColor = (status) => {
    if (status === "TENANT_ACCEPTED") return "#16A34A"; // Green
    if (status === "REJECTED") return "#DC2626"; // Red
    if (status === "DRAFT") return "#F59E0B"; // Yellow
    if (status === "Generate") return "#6B7280"; // Gray
    return "#6B7280";
  };

  // NEW: Check if footer should be shown
  const shouldShowFooter = () => {
    return agreementStatus !== "TENANT_ACCEPTED" && agreementStatus !== "REJECTED";
  };

  // NEW: Check if Save to Cloud button should be shown
  const shouldShowSaveToCloud = () => {
    return agreementStatus === "TENANT_ACCEPTED";
  };

  // NEW: Check if tenant signature should be shown
  const shouldShowTenantSignature = () => {
    return agreementStatus === "TENANT_ACCEPTED";
  };

  const shouldShowOwnerTenantSignature = () => {
    return withOrWithoutOtp === true && agreementStatus === "TENANT_ACCEPTED";
  };

  // Fetch agreement data function - made reusable
  const fetchAgreementData = async () => {
    if (!decryptedParams) return;
    
    setLoading(true);
    setError(null);
    setActionMessage(null);

    try {
      const { agreementId, tenantId, pgId } = decryptedParams;
      
      const finalPgId = pgId || 8746;
      const finalTenantId = tenantId || 1025;

      console.log('Fetching agreement data with decrypted params:', { agreementId, tenantId, pgId });

      const response = await getAgreementJsonData(finalPgId, finalTenantId);
      
      console.log('Agreement response:', response);

      if (response.success && response.data) {
        let parsedData = response.data;
        let agreementJsonData = null;
        
        // NEW: Store agreement status
        if (response.data.AgreementStatus) {
          setAgreementStatus(response.data.AgreementStatus);
          console.log('Agreement Status:', response.data.AgreementStatus);
        }
        
        if (response.data.WithorWithoutOtp !== undefined) {
          setWithOrWithoutOtp(response.data.WithorWithoutOtp);
          console.log('WithorWithoutOtp:', response.data.WithorWithoutOtp);
        }
        
        // Parse agreementjson if it's a string
        if (typeof response.data.agreementjson === 'string') {
          try {
            const jsonData = JSON.parse(response.data.agreementjson);
            if (Array.isArray(jsonData) && jsonData.length > 0) {
              agreementJsonData = jsonData[0];
            } else {
              agreementJsonData = jsonData;
            }
            parsedData = {
              ...response.data,
              agreementjson: agreementJsonData
            };
          } catch (parseError) {
            console.error('Error parsing agreement JSON:', parseError);
            parsedData.agreementjson = response.data.agreementjson;
          }
        } else if (Array.isArray(response.data.agreementjson)) {
          agreementJsonData = response.data.agreementjson[0] || response.data.agreementjson;
          parsedData = {
            ...response.data,
            agreementjson: agreementJsonData
          };
        } else {
          agreementJsonData = response.data.agreementjson;
        }

        setAgreementData(parsedData);
        setShowModal(true);
        
        const mobile = getTenantMobileFromData(parsedData);
        console.log("Extracted mobile:", mobile);
        
        if (mobile && mobile.length >= 10) {
          setOtpMobile(mobile);
          console.log("OTP Mobile set to:", mobile);
        } else {
          console.warn("No valid mobile found in agreement data");
          setOtpMobile('');
        }
      } else {
        setError(response.message || 'Failed to fetch agreement data');
      }
    } catch (err) {
      console.error('Error fetching agreement:', err);
      setError(err.message || 'An error occurred while fetching agreement');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchAgreementData();
  }, [decryptedParams]);

  // Helper function to extract mobile from agreement data
  const getTenantMobileFromData = (data) => {
    if (!data) return '';
    
    const agreementData = data.agreementjson || data;
    console.log("Agreement data for mobile extraction:", agreementData);
    
    let sourceData = agreementData;
    if (Array.isArray(sourceData) && sourceData.length > 0) {
      sourceData = sourceData[0];
    }
    
    let tenantData = null;
    
    if (sourceData?.tenant) {
      tenantData = sourceData.tenant;
    } else if (sourceData?.Tenant) {
      tenantData = sourceData.Tenant;
    } else if (sourceData?.tenants) {
      tenantData = sourceData.tenants;
    }
    
    console.log("Tenant data found:", tenantData);
    
    if (tenantData) {
      let tenantObject = tenantData;
      if (Array.isArray(tenantData) && tenantData.length > 0) {
        tenantObject = tenantData[0];
        console.log("Tenant data is array, using first element:", tenantObject);
      }
      
      const mobile = 
        tenantObject?.mobile || 
        tenantObject?.Mobile || 
        tenantObject?.PhoneNumber || 
        tenantObject?.phone || 
        tenantObject?.mobileNumber || 
        tenantObject?.Phone || 
        tenantObject?.phoneNumber ||
        '';
      
      console.log("Mobile found:", mobile);
      
      if (mobile && typeof mobile === 'string' && mobile.length >= 10) {
        return mobile;
      }
      
      if (mobile && typeof mobile === 'number') {
        const mobileStr = mobile.toString();
        if (mobileStr.length >= 10) {
          return mobileStr;
        }
      }
    }
    
    // Search in entire object
    const allKeys = Object.keys(sourceData);
    for (const key of allKeys) {
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes('mobile') || lowerKey.includes('phone') || lowerKey.includes('contact')) {
        const value = sourceData[key];
        if (value) {
          if (typeof value === 'string') {
            const cleaned = value.replace(/\D/g, '');
            if (cleaned.length >= 10) {
              console.log("Found mobile in key:", key, value);
              return value;
            }
          }
          if (typeof value === 'number') {
            const valueStr = value.toString();
            if (valueStr.length >= 10) {
              console.log("Found mobile in key:", key, value);
              return valueStr;
            }
          }
        }
      }
    }
    
    return '';
  };

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [resendTimer]);

  // Handle PDF upload success
  const handlePdfUploadSuccess = (data) => {
    console.log('PDF uploaded successfully:', data);
    setPdfUploaded(true);
    setPdfUploadData(data);
    
    setActionMessage({
      type: 'success',
      text: '✅ Agreement PDF saved to cloud storage successfully!'
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Handle sending OTP
  const handleSendOtp = async (purpose) => {
    if (!agreementConsent) {
      setActionMessage({
        type: 'error',
        text: 'Please accept the terms and conditions before proceeding.'
      });
      return;
    }

    const mobileNumber = otpMobile || getTenantMobile();
    if (!mobileNumber || mobileNumber.length < 10) {
      setOtpError('Invalid mobile number. Please contact support.');
      return;
    }

    if (!otpMobile && mobileNumber) {
      setOtpMobile(mobileNumber);
    }

    setOtpPurpose(purpose);
    setIsSendingOtp(true);
    setOtpError('');
    setOtpSuccess('');
    setOtpVerified(false);
    setOtp('');
    setOtpId(null);

    try {
      const sendOtpResponse = await sendMsg91Otp({
        mobile: `91${mobileNumber}`,
        param1: 'OTP',
        param2: 'Agreement Verification'
      });

      console.log('Send OTP Response:', sendOtpResponse);

      if (sendOtpResponse.success) {
        setOtpSent(true);
        setOtpSuccess('OTP sent successfully to your mobile number!');
        setResendTimer(30);
      } else {
        setOtpError(sendOtpResponse.message || 'Failed to send OTP');
      }
    } catch (err) {
      console.error('Error sending OTP:', err);
      setOtpError(err.message || 'An error occurred while sending OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle verifying OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 4) {
      setOtpError('Please enter a valid 4-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');
    setOtpSuccess('');

    try {
      const verifyResponse = await verifyMsg91Otp(`91${otpMobile}`, otp);
      console.log('Verify OTP Response:', verifyResponse);

      if (verifyResponse.success) {
        const generateResponse = await generateOtpmsg91(
          otpMobile,
          'RentAgreement',
          otp,
          decryptedParams?.pgId || 8746,
          getAgreementID()
        );

        console.log('Generate OTP in DB Response:', generateResponse);

        if (generateResponse.success) {
          const verifyPayload = {
            MobileNumber: otpMobile,
            OTPCode: otp,
            Purpose: 'RentAgreement',
            FullName: '',
            Email: '',
            ReferenceId: parseInt(getAgreementID())
          };
          const verifyResponse = await verifyOTPAgreement(verifyPayload);

          if (verifyResponse.Success == 1) {
            console.log('OTP verified with ID:', otpId);
            console.log("otpid", generateResponse.data?.otpId);
            setOtpId(generateResponse.data?.otpId);
            setOtpVerified(true);
            setOtpSuccess('OTP verified successfully!');

            setTimeout(() => {
              setShowOtpModal(false);
              if (otpPurpose === 'approve') {
                executeApproveAgreement(generateResponse.data?.otpId);
              } else if (otpPurpose === 'reject') {
                setShowRejectReasonModal(true);
              }
            }, 1000);
          }
        } else {
          setOtpError(generateResponse.message || 'Failed to save OTP verification');
        }
      } else {
        setOtpError(verifyResponse.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setOtpError(err.message || 'An error occurred while verifying OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setOtpError('');
    setOtpSuccess('');
    setIsSendingOtp(true);

    try {
      const sendOtpResponse = await sendMsg91Otp({
        mobile: `91${otpMobile}`,
        param1: 'OTP',
        param2: 'Agreement Verification'
      });

      if (sendOtpResponse.success) {
        setOtpSuccess('OTP resent successfully!');
        setResendTimer(30);
      } else {
        setOtpError(sendOtpResponse.message || 'Failed to resend OTP');
      }
    } catch (err) {
      console.error('Error resending OTP:', err);
      setOtpError(err.message || 'An error occurred while resending OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Execute Approve Agreement
  const executeApproveAgreement = async (otpid) => {
    console.log("generateResponse.data?.otpId approve", otpid);
    setIsSubmitting(true);
    setActionMessage(null);

    try {
      const agreementID = getAgreementID();
      
      const acceptData = {
        AgreementID: parseInt(agreementID),
        OTPID: parseInt(otpid),
        IPAddress: '192.168.1.100',
        DeviceInfo: navigator.userAgent || 'Chrome Browser',
        Latitude: "28.613939",
        Longitude: "77.209021"
      };

      console.log('Accepting agreement with data:', acceptData);

      const response = await acceptRentAgreement(acceptData);

      console.log('Accept Agreement Response:', response);

      if (response.success) {
        setActionSuccess(true);
        setActionMessage({
          type: 'success',
          text: '🎉 Agreement accepted successfully! You can now download the signed agreement.'
        });

        // NEW: Refresh agreement data to get updated status
        setTimeout(async () => {
          await fetchAgreementData();
        }, 2000);
      } else {
        setActionMessage({
          type: 'error',
          text: response.message || 'Failed to accept agreement'
        });
      }
    } catch (err) {
      console.error('Error accepting agreement:', err);
      setActionMessage({
        type: 'error',
        text: err.message || 'An error occurred while accepting the agreement'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Execute Reject Agreement
  const executeRejectAgreement = async () => {
    console.log("reject otpid", otpId);
    if (!rejectReason || rejectReason.trim().length === 0) {
      setRejectError('Please provide a reason for rejection');
      return;
    }

    setIsSubmitting(true);
    setActionMessage(null);
    setRejectError('');

    try {
      const agreementID = getAgreementID();
      
      const rejectData = {
        AgreementID: parseInt(agreementID),
        RejectReason: rejectReason.trim(),
        IPAddress: '192.168.1.100',
        DeviceInfo: navigator.userAgent || 'Chrome Browser'
      };

      console.log('Rejecting agreement with data:', rejectData);

      const response = await rejectRentAgreement(rejectData);

      console.log('Reject Agreement Response:', response);

      if (response.success) {
        setActionSuccess(false);
        setActionMessage({
          type: 'error',
          text: '❌ Agreement rejected. You can contact the landlord for further discussion.'
        });

        setShowRejectReasonModal(false);
        
        // NEW: Refresh agreement data to get updated status
        setTimeout(async () => {
          await fetchAgreementData();
        }, 2000);
        
        setTimeout(() => handleCloseModal(), 3000);
      } else {
        setActionMessage({
          type: 'error',
          text: response.message || 'Failed to reject agreement'
        });
      }
    } catch (err) {
      console.error('Error rejecting agreement:', err);
      setActionMessage({
        type: 'error',
        text: err.message || 'An error occurred while rejecting the agreement'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Approve button click
  const handleApproveClick = async () => {
    // Check if OTP is required (WithorWithoutOtp === true)
    if (withOrWithoutOtp === true) {
      // OTP flow - show OTP modal
      if (!otpMobile) {
        const mobile = getTenantMobile();
        if (mobile) {
          setOtpMobile(mobile);
        } else {
          setActionMessage({
            type: 'error',
            text: 'No mobile number found for this tenant. Please contact support.'
          });
          return;
        }
      }

      setShowOtpModal(true);
      setOtpPurpose('approve');
      setOtpError('');
      setOtpSuccess('');
      setOtp('');
      setOtpSent(false);
      setOtpVerified(false);
      setOtpId(null);
      setTimeout(() => {
        handleSendOtp('approve');
      }, 500);
    } else {
      // Without OTP flow - directly execute approval
      // Show confirmation popup first
      if (window.confirm('Are you sure you want to approve this agreement without OTP verification?')) {
       

        const generateResponse = await generateOtpmsg91(
          otpMobile,
          'RentAgreement',
          otp || '0000', 
          decryptedParams?.pgId || 8746,
          getAgreementID()
        );

        console.log('Generate OTP in DB Response:', generateResponse);

        if (generateResponse.success) {
          const verifyPayload = {
            MobileNumber: otpMobile,
            OTPCode: otp || '0000',
            Purpose: 'RentAgreement',
            FullName: '',
            Email: '',
            ReferenceId: parseInt(getAgreementID())
          };
          const verifyResponse = await verifyOTPAgreement(verifyPayload);

          if (verifyResponse.Success == 1) {
            console.log('OTP verified with ID:', otpId);
            console.log("otpid", generateResponse.data?.otpId);
            setOtpId(generateResponse.data?.otpId);
            // setOtpVerified(true);
            // setOtpSuccess('OTP verified successfully!');

            setTimeout(() => {
            //   // setShowOtpModal(false);
            //   if (otpPurpose === 'approve') {
                executeApproveAgreement(generateResponse.data?.otpId);
              // } else if (otpPurpose === 'reject') {
              //   setShowRejectReasonModal(true);
              // }
            }, 1000);
          }
        } else {
          setOtpError(generateResponse.message || 'Failed to Approve Agreement');
        }
       
      }
    }
  };

  // Handle Reject button click
  const handleRejectClick = () => {
    // Check if OTP is required (WithorWithoutOtp === true)
    if (withOrWithoutOtp === true) {
      // OTP flow - show OTP modal
      if (!otpMobile) {
        const mobile = getTenantMobile();
        if (mobile) {
          setOtpMobile(mobile);
        } else {
          setActionMessage({
            type: 'error',
            text: 'No mobile number found for this tenant. Please contact support.'
          });
          return;
        }
      }

      setShowOtpModal(true);
      setOtpPurpose('reject');
      setOtpError('');
      setOtpSuccess('');
      setOtp('');
      setOtpSent(false);
      setOtpVerified(false);
      setOtpId(null);
      setTimeout(() => {
        handleSendOtp('reject');
      }, 500);
    } else {
      // Without OTP flow - directly show reject reason modal
      setShowRejectReasonModal(true);
      setRejectReason('');
      setRejectError('');
    }
  };

  // Close OTP modal
  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setOtpError('');
    setOtpSuccess('');
    setOtp('');
    setOtpSent(false);
    setOtpVerified(false);
    setOtpId(null);
  };

  if (loading) {
    return (
      <div className="agreement-view-loading">
        <div className="agreement-view-spinner"></div>
        <p>Loading your agreement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agreement-view-error">
        <div className="agreement-view-error-content">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h2>Unable to Load Agreement</h2>
          <p>{error}</p>
          <button 
            className="agreement-view-retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
          <button 
            className="agreement-view-home-btn"
            onClick={() => window.location.href = '/'}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const tenantName = getTenantName();
  const statusText = getAgreementStatusText(agreementStatus);
  const statusColor = getAgreementStatusColor(agreementStatus);

  return (
    <div className="agreement-view-container">
      {/* Success/Error Message */}
      {actionMessage && (
        <div className={`agreement-view-message ${actionMessage.type === 'success' ? 'agreement-view-message-success' : 'agreement-view-message-error'}`}>
          {actionMessage.text}
          {actionMessage.type === 'success' && actionSuccess && (
            <button 
              className="agreement-view-message-close"
              onClick={() => setActionMessage(null)}
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* Agreement Modal - UPDATED with new props */}
      {showModal && agreementData && (
        <ViewAgreementModal
          data={agreementData.agreementjson || agreementData}
          fileName={`Agreement_${agreementData.AgreementNo || 'Rent'}.pdf`}
          onClose={handleCloseModal}
          // isVerified={shouldShowTenantSignature()} // NEW: Based on status
          isVerified={shouldShowOwnerTenantSignature()} // NEW: Based on status
          agreementId={getAgreementID()}
          onUploadSuccess={handlePdfUploadSuccess}
          showSaveToCloud={shouldShowSaveToCloud()} // NEW: Pass this prop
          agreementStatus={agreementStatus} // NEW: Pass status
          statusText={statusText} // NEW: Pass status text
          statusColor={statusColor} // NEW: Pass status color
        />
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="agreement-otp-overlay">
          <div className="agreement-otp-modal">
            <div className="agreement-otp-header">
              <h3>Verify Your Identity</h3>
              <button className="agreement-otp-close" onClick={handleCloseOtpModal}>
                ×
              </button>
            </div>
            <div className="agreement-otp-body">
              <p className="agreement-otp-info">
                We've sent a 4-digit verification code to <strong>{otpMobile}</strong>
              </p>
              <p className="agreement-otp-purpose">
                Purpose: {otpPurpose === 'approve' ? 'Approve Agreement' : 'Reject Agreement'}
              </p>

              {otpError && (
                <div className="agreement-otp-error">{otpError}</div>
              )}
              {otpSuccess && (
                <div className="agreement-otp-success">{otpSuccess}</div>
              )}

              <div className="agreement-otp-input-group">
                <input
                  type="text"
                  className="agreement-otp-input"
                  placeholder="Enter 4-digit OTP"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 4) {
                      setOtp(value);
                    }
                  }}
                  maxLength="4"
                  disabled={isVerifyingOtp || otpVerified}
                  autoFocus
                />
              </div>

              <div className="agreement-otp-actions">
                <button
                  className="agreement-otp-btn agreement-otp-btn-verify"
                  onClick={handleVerifyOtp}
                  disabled={isVerifyingOtp || otpVerified || otp.length !== 4}
                >
                  {isVerifyingOtp ? 'Verifying...' : otpVerified ? '✓ Verified' : 'Verify OTP'}
                </button>
              </div>

              <div className="agreement-otp-resend">
                <button
                  className="agreement-otp-btn-resend"
                  onClick={handleResendOtp}
                  disabled={isSendingOtp || resendTimer > 0 || otpVerified}
                >
                  {isSendingOtp ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {showRejectReasonModal && (
        <div className="agreement-reject-overlay">
          <div className="agreement-reject-modal">
            <div className="agreement-reject-header">
              <h3>Reason for Rejection</h3>
              <button 
                className="agreement-reject-close" 
                onClick={() => setShowRejectReasonModal(false)}
              >
                ×
              </button>
            </div>
            <div className="agreement-reject-body">
              <p className="agreement-reject-info">
                Please provide a reason for rejecting this agreement:
              </p>
              {rejectError && (
                <div className="agreement-reject-error">{rejectError}</div>
              )}
              <textarea
                className="agreement-reject-textarea"
                placeholder="Enter your reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows="4"
                disabled={isSubmitting}
              />
              <div className="agreement-reject-actions">
                <button
                  className="agreement-reject-btn agreement-reject-btn-cancel"
                  onClick={() => setShowRejectReasonModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="agreement-reject-btn agreement-reject-btn-submit"
                  onClick={executeRejectAgreement}
                  disabled={isSubmitting || !rejectReason.trim()}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Rejection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer with Checkbox and Buttons - Only show if status is DRAFT or Generate */}
      {shouldShowFooter() && (
        <div className="agreement-view-footer">
          <div className="agreement-view-footer-content">
            <label className="agreement-view-consent">
              <input
                type="checkbox"
                checked={agreementConsent}
                onChange={(e) => {
                  setAgreementConsent(e.target.checked);
                  if (actionMessage) setActionMessage(null);
                }}
                disabled={isSubmitting}
              />
              <span>
                I, <strong>{tenantName}</strong>, have read and agree to the terms of the Rent Agreement above.
              </span>
            </label>

            <div className="agreement-view-actions">
              <button
                className="agreement-view-btn agreement-view-btn-reject"
                onClick={handleRejectClick}
                disabled={isSubmitting || !agreementConsent}
              >
                {isSubmitting ? (
                  <>
                    <span className="agreement-view-spinner-small"></span>
                    Processing...
                  </>
                ) : (
                  '✕ Reject'
                )}
              </button>
              <button
                className="agreement-view-btn agreement-view-btn-approve"
                onClick={handleApproveClick}
                disabled={isSubmitting || !agreementConsent}
              >
                {isSubmitting ? (
                  <>
                    <span className="agreement-view-spinner-small"></span>
                    Processing...
                  </>
                ) : (
                  '✓ Approve'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgreementView;


// // src/components/AgreementView.jsx
// import React, { useState, useEffect, useRef } from 'react';
// import { useParams } from 'react-router-dom'; // Remove useSearchParams
// import { 
//   getAgreementJsonData,
//   sendMsg91Otp,
//   verifyMsg91Otp,
//   generateOtpmsg91,
//   acceptRentAgreement,
//   rejectRentAgreement,
//   verifyOTPAgreement
// } from '../apis/services/pgApi';
// import ViewAgreementModal from './RentAgreementDynamic';
// import '../scss/components/_agreementview.scss';

// // Import the encryption service or create a utility
// // import { decryptAgreementParams } from '../utils/encryption';


// // src/utils/encryption.js
// import * as CryptoJS from 'crypto-js';

// const SECRET_KEY = 'cipl';

// // Generate SHA256 hash of the secret key (matching backend)
// const getSecretKey = () => {
//   return CryptoJS.SHA256(SECRET_KEY).toString();
// };

// // Decrypt agreement parameters
// export const decryptAgreementParams = (encryptedParams) => {
//   try {
//     // Split the encrypted data (IV:encrypted)
//     const parts = encryptedParams.split(':');
//     if (parts.length !== 2) {
//       console.error('Invalid encrypted format');
//       return null;
//     }

//     const [ivHex, encryptedData] = parts;
    
//     // Convert hex strings to WordArrays
//     const iv = CryptoJS.enc.Hex.parse(ivHex);
//     const encrypted = CryptoJS.enc.Hex.parse(encryptedData);

//     // Create decryption key
//     const key = CryptoJS.enc.Hex.parse(getSecretKey());

//     // Decrypt
//     const decrypted = CryptoJS.AES.decrypt(
//       { ciphertext: encrypted },
//       key,
//       {
//         iv: iv,
//         mode: CryptoJS.mode.CBC,
//         padding: CryptoJS.pad.Pkcs7
//       }
//     );

//     const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    
//     if (!decryptedText) {
//       console.error('Decryption failed');
//       return null;
//     }

//     // Parse the decrypted string (format: agreementId|tenantId|pgId)
//     const params = decryptedText.split('|');
//     if (params.length !== 3) {
//       console.error('Invalid decrypted format');
//       return null;
//     }

//     return {
//       agreementId: parseInt(params[0]),
//       tenantId: parseInt(params[1]),
//       pgId: parseInt(params[2])
//     };
//   } catch (error) {
//     console.error('Decryption error:', error);
//     return null;
//   }
// };


// const AgreementView = () => {
//   const { encryptedParams } = useParams(); // Get encrypted params from URL
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [agreementData, setAgreementData] = useState(null);
//   const [showModal, setShowModal] = useState(false);
  
//   // Decrypted parameters
//   const [decryptedParams, setDecryptedParams] = useState(null);
  
//   // Agreement consent states
//   const [agreementConsent, setAgreementConsent] = useState(false);
  
//   // OTP related states
//   const [showOtpModal, setShowOtpModal] = useState(false);
//   const [otp, setOtp] = useState('');
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpId, setOtpId] = useState(null);
//   const [otpPurpose, setOtpPurpose] = useState('');
//   const [otpMobile, setOtpMobile] = useState('');
//   const [otpError, setOtpError] = useState('');
//   const [otpSuccess, setOtpSuccess] = useState('');
//   const [isSendingOtp, setIsSendingOtp] = useState(false);
//   const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
//   const [resendTimer, setResendTimer] = useState(0);
//   const [otpVerified, setOtpVerified] = useState(false);
  
//   // For reject reason
//   const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
//   const [rejectReason, setRejectReason] = useState('');
//   const [rejectError, setRejectError] = useState('');

//   // Action states
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [actionMessage, setActionMessage] = useState(null);
//   const [actionSuccess, setActionSuccess] = useState(false);

//   // NEW: State for PDF upload
//   const [pdfUploaded, setPdfUploaded] = useState(false);
//   const [pdfUploadData, setPdfUploadData] = useState(null);

//   const timerRef = useRef(null);

//   // Decrypt parameters on component mount
//   useEffect(() => {
//     if (encryptedParams) {
//       const params = decryptAgreementParams(encryptedParams);
//       if (params) {
//         setDecryptedParams(params);
//         console.log('Decrypted params:', params);
//       } else {
//         setError('Invalid or expired agreement link');
//         setLoading(false);
//       }
//     } else {
//       setError('Invalid agreement link');
//       setLoading(false);
//     }
//   }, [encryptedParams]);

//   // Get the tenant name from agreement data
//   const getTenantName = () => {
//     if (!agreementData) return 'Tenant';
//     const data = agreementData.agreementjson || agreementData;
//     const tenantData = data?.tenant || {};
//     return tenantData[0]?.name || tenantData?.Name || 'Tenant';
//   };

//   // Get tenant mobile from agreement data
//   const getTenantMobile = () => {
//     if (!agreementData) return '';
//     const data = agreementData.agreementjson || agreementData;
    
//     if (Array.isArray(data)) {
//       const firstItem = data[0] || {};
//       const tenantData = firstItem?.tenant || {};
//       return tenantData?.mobile || tenantData?.Mobile || '';
//     }
    
//     const tenantData = data?.tenant || {};
//     return tenantData?.mobile || tenantData?.Mobile || '';
//   };

//   // Get agreement ID
//   const getAgreementID = () => {
//     return agreementData?.AgreementID || decryptedParams?.agreementId;
//   };

//   useEffect(() => {
//     const fetchAgreementData = async () => {
//       if (!decryptedParams) return;
      
//       setLoading(true);
//       setError(null);
//       setActionMessage(null);

//       try {
//         const { agreementId, tenantId, pgId } = decryptedParams;
        
//         // Use the IDs from decrypted params
//         const finalPgId = pgId || 8746;
//         const finalTenantId = tenantId || 1025;

//         console.log('Fetching agreement data with decrypted params:', { agreementId, tenantId, pgId });

//         const response = await getAgreementJsonData(finalPgId, finalTenantId);
        
//         console.log('Agreement response:', response);

//         if (response.success && response.data) {
//           let parsedData = response.data;
//           let agreementJsonData = null;
          
//           // Parse agreementjson if it's a string
//           if (typeof response.data.agreementjson === 'string') {
//             try {
//               const jsonData = JSON.parse(response.data.agreementjson);
//               if (Array.isArray(jsonData) && jsonData.length > 0) {
//                 agreementJsonData = jsonData[0];
//               } else {
//                 agreementJsonData = jsonData;
//               }
//               parsedData = {
//                 ...response.data,
//                 agreementjson: agreementJsonData
//               };
//             } catch (parseError) {
//               console.error('Error parsing agreement JSON:', parseError);
//               parsedData.agreementjson = response.data.agreementjson;
//             }
//           } else if (Array.isArray(response.data.agreementjson)) {
//             agreementJsonData = response.data.agreementjson[0] || response.data.agreementjson;
//             parsedData = {
//               ...response.data,
//               agreementjson: agreementJsonData
//             };
//           } else {
//             agreementJsonData = response.data.agreementjson;
//           }

//           setAgreementData(parsedData);
//           setShowModal(true);
          
//           const mobile = getTenantMobileFromData(parsedData);
//           console.log("Extracted mobile:", mobile);
          
//           if (mobile && mobile.length >= 10) {
//             setOtpMobile(mobile);
//             console.log("OTP Mobile set to:", mobile);
//           } else {
//             console.warn("No valid mobile found in agreement data");
//             setOtpMobile('');
//           }
//         } else {
//           setError(response.message || 'Failed to fetch agreement data');
//         }
//       } catch (err) {
//         console.error('Error fetching agreement:', err);
//         setError(err.message || 'An error occurred while fetching agreement');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAgreementData();
//   }, [decryptedParams]);

//   // Helper function to extract mobile from agreement data
//   const getTenantMobileFromData = (data) => {
//     if (!data) return '';
    
//     const agreementData = data.agreementjson || data;
//     console.log("Agreement data for mobile extraction:", agreementData);
    
//     let sourceData = agreementData;
//     if (Array.isArray(sourceData) && sourceData.length > 0) {
//       sourceData = sourceData[0];
//     }
    
//     let tenantData = null;
    
//     if (sourceData?.tenant) {
//       tenantData = sourceData.tenant;
//     } else if (sourceData?.Tenant) {
//       tenantData = sourceData.Tenant;
//     } else if (sourceData?.tenants) {
//       tenantData = sourceData.tenants;
//     }
    
//     console.log("Tenant data found:", tenantData);
    
//     if (tenantData) {
//       let tenantObject = tenantData;
//       if (Array.isArray(tenantData) && tenantData.length > 0) {
//         tenantObject = tenantData[0];
//         console.log("Tenant data is array, using first element:", tenantObject);
//       }
      
//       const mobile = 
//         tenantObject?.mobile || 
//         tenantObject?.Mobile || 
//         tenantObject?.PhoneNumber || 
//         tenantObject?.phone || 
//         tenantObject?.mobileNumber || 
//         tenantObject?.Phone || 
//         tenantObject?.phoneNumber ||
//         '';
      
//       console.log("Mobile found:", mobile);
      
//       if (mobile && typeof mobile === 'string' && mobile.length >= 10) {
//         return mobile;
//       }
      
//       if (mobile && typeof mobile === 'number') {
//         const mobileStr = mobile.toString();
//         if (mobileStr.length >= 10) {
//           return mobileStr;
//         }
//       }
//     }
    
//     // Search in entire object
//     const allKeys = Object.keys(sourceData);
//     for (const key of allKeys) {
//       const lowerKey = key.toLowerCase();
//       if (lowerKey.includes('mobile') || lowerKey.includes('phone') || lowerKey.includes('contact')) {
//         const value = sourceData[key];
//         if (value) {
//           if (typeof value === 'string') {
//             const cleaned = value.replace(/\D/g, '');
//             if (cleaned.length >= 10) {
//               console.log("Found mobile in key:", key, value);
//               return value;
//             }
//           }
//           if (typeof value === 'number') {
//             const valueStr = value.toString();
//             if (valueStr.length >= 10) {
//               console.log("Found mobile in key:", key, value);
//               return valueStr;
//             }
//           }
//         }
//       }
//     }
    
//     return '';
//   };

//   // Timer for resend OTP
//   useEffect(() => {
//     if (resendTimer > 0) {
//       timerRef.current = setTimeout(() => {
//         setResendTimer(resendTimer - 1);
//       }, 1000);
//     }
//     return () => {
//       if (timerRef.current) {
//         clearTimeout(timerRef.current);
//       }
//     };
//   }, [resendTimer]);

//   // NEW: Handle PDF upload success
//   const handlePdfUploadSuccess = (data) => {
//     console.log('PDF uploaded successfully:', data);
//     setPdfUploaded(true);
//     setPdfUploadData(data);
    
//     // Show success message
//     setActionMessage({
//       type: 'success',
//       text: '✅ Agreement PDF saved to cloud storage successfully!'
//     });
//   };

//   const handleCloseModal = () => {
//     setShowModal(false);
//   };

//   // Handle sending OTP
//   const handleSendOtp = async (purpose) => {
//     if (!agreementConsent) {
//       setActionMessage({
//         type: 'error',
//         text: 'Please accept the terms and conditions before proceeding.'
//       });
//       return;
//     }

//     const mobileNumber = otpMobile || getTenantMobile();
//     if (!mobileNumber || mobileNumber.length < 10) {
//       setOtpError('Invalid mobile number. Please contact support.');
//       return;
//     }

//     if (!otpMobile && mobileNumber) {
//       setOtpMobile(mobileNumber);
//     }

//     setOtpPurpose(purpose);
//     setIsSendingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtpVerified(false);
//     setOtp('');
//     setOtpId(null);

//     try {
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: `91${mobileNumber}`,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       console.log('Send OTP Response:', sendOtpResponse);

//       if (sendOtpResponse.success) {
//         setOtpSent(true);
//         setOtpSuccess('OTP sent successfully to your mobile number!');
//         setResendTimer(30);
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to send OTP');
//       }
//     } catch (err) {
//       console.error('Error sending OTP:', err);
//       setOtpError(err.message || 'An error occurred while sending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Handle verifying OTP
//   const handleVerifyOtp = async () => {
//     if (!otp || otp.length !== 4) {
//       setOtpError('Please enter a valid 4-digit OTP');
//       return;
//     }

//     setIsVerifyingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');

//     try {
//       const verifyResponse = await verifyMsg91Otp(`91${otpMobile}`, otp);
//       console.log('Verify OTP Response:', verifyResponse);

//       if (verifyResponse.success) {
//         const generateResponse = await generateOtpmsg91(
//           otpMobile,
//           'RentAgreement',
//           otp,
//           decryptedParams?.pgId || 8746,
//           getAgreementID()
//         );

//         console.log('Generate OTP in DB Response:', generateResponse);

//         if (generateResponse.success) {
//           const verifyPayload = {
//             MobileNumber: otpMobile,
//             OTPCode: otp,
//             Purpose: 'RentAgreement',
//             FullName: '',
//             Email: '',
//             ReferenceId: parseInt(getAgreementID())
//           };
//           const verifyResponse = await verifyOTPAgreement(verifyPayload);

//           if (verifyResponse.Success == 1) {
//             console.log('OTP verified with ID:', otpId);
//             console.log("otpid", generateResponse.data?.otpId);
//             setOtpId(generateResponse.data?.otpId);
//             setOtpVerified(true);
//             setOtpSuccess('OTP verified successfully!');

//             setTimeout(() => {
//               setShowOtpModal(false);
//               if (otpPurpose === 'approve') {
//                 executeApproveAgreement(generateResponse.data?.otpId);
//               } else if (otpPurpose === 'reject') {
//                 setShowRejectReasonModal(true);
//               }
//             }, 1000);
//           }
//         } else {
//           setOtpError(generateResponse.message || 'Failed to save OTP verification');
//         }
//       } else {
//         setOtpError(verifyResponse.message || 'Invalid OTP. Please try again.');
//       }
//     } catch (err) {
//       console.error('Error verifying OTP:', err);
//       setOtpError(err.message || 'An error occurred while verifying OTP');
//     } finally {
//       setIsVerifyingOtp(false);
//     }
//   };

//   // Handle resend OTP
//   const handleResendOtp = async () => {
//     if (resendTimer > 0) return;
    
//     setOtpError('');
//     setOtpSuccess('');
//     setIsSendingOtp(true);

//     try {
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: `91${otpMobile}`,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       if (sendOtpResponse.success) {
//         setOtpSuccess('OTP resent successfully!');
//         setResendTimer(30);
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to resend OTP');
//       }
//     } catch (err) {
//       console.error('Error resending OTP:', err);
//       setOtpError(err.message || 'An error occurred while resending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Execute Approve Agreement
//   const executeApproveAgreement = async (otpid) => {
//     console.log("generateResponse.data?.otpId approve", otpid);
//     setIsSubmitting(true);
//     setActionMessage(null);

//     try {
//       const agreementID = getAgreementID();
      
//       const acceptData = {
//         AgreementID: parseInt(agreementID),
//         OTPID: parseInt(otpid),
//         IPAddress: '192.168.1.100',
//         DeviceInfo: navigator.userAgent || 'Chrome Browser',
//         Latitude: "28.613939",
//         Longitude: "77.209021"
//       };

//       console.log('Accepting agreement with data:', acceptData);

//       const response = await acceptRentAgreement(acceptData);

//       console.log('Accept Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(true);
//         setActionMessage({
//           type: 'success',
//           text: '🎉 Agreement accepted successfully! You can now download the signed agreement.'
//         });

//         // Set verified state to true to show signature in PDF
//         setOtpVerified(true);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to accept agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error accepting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while accepting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Execute Reject Agreement
//   const executeRejectAgreement = async () => {
//     console.log("reject otpid", otpId);
//     if (!rejectReason || rejectReason.trim().length === 0) {
//       setRejectError('Please provide a reason for rejection');
//       return;
//     }

//     setIsSubmitting(true);
//     setActionMessage(null);
//     setRejectError('');

//     try {
//       const agreementID = getAgreementID();
      
//       const rejectData = {
//         AgreementID: parseInt(agreementID),
//         RejectReason: rejectReason.trim(),
//         IPAddress: '192.168.1.100',
//         DeviceInfo: navigator.userAgent || 'Chrome Browser'
//       };

//       console.log('Rejecting agreement with data:', rejectData);

//       const response = await rejectRentAgreement(rejectData);

//       console.log('Reject Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(false);
//         setActionMessage({
//           type: 'error',
//           text: '❌ Agreement rejected. You can contact the landlord for further discussion.'
//         });

//         setShowRejectReasonModal(false);
//         setTimeout(() => handleCloseModal(), 3000);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to reject agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error rejecting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while rejecting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Handle Approve button click
//   const handleApproveClick = () => {
//     if (!otpMobile) {
//       const mobile = getTenantMobile();
//       if (mobile) {
//         setOtpMobile(mobile);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: 'No mobile number found for this tenant. Please contact support.'
//         });
//         return;
//       }
//     }
    
//     setShowOtpModal(true);
//     setOtpPurpose('approve');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//     setTimeout(() => {
//       handleSendOtp('approve');
//     }, 500);
//   };

//   // Handle Reject button click
//   const handleRejectClick = () => {
//     if (!otpMobile) {
//       const mobile = getTenantMobile();
//       if (mobile) {
//         setOtpMobile(mobile);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: 'No mobile number found for this tenant. Please contact support.'
//         });
//         return;
//       }
//     }
    
//     setShowOtpModal(true);
//     setOtpPurpose('reject');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//     setTimeout(() => {
//       handleSendOtp('reject');
//     }, 500);
//   };

//   // Close OTP modal
//   const handleCloseOtpModal = () => {
//     setShowOtpModal(false);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//   };

//   if (loading) {
//     return (
//       <div className="agreement-view-loading">
//         <div className="agreement-view-spinner"></div>
//         <p>Loading your agreement...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="agreement-view-error">
//         <div className="agreement-view-error-content">
//           <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
//             <circle cx="12" cy="12" r="10" />
//             <line x1="12" y1="8" x2="12" y2="12" />
//             <line x1="12" y1="16" x2="12.01" y2="16" />
//           </svg>
//           <h2>Unable to Load Agreement</h2>
//           <p>{error}</p>
//           <button 
//             className="agreement-view-retry-btn"
//             onClick={() => window.location.reload()}
//           >
//             Retry
//           </button>
//           <button 
//             className="agreement-view-home-btn"
//             onClick={() => window.location.href = '/'}
//           >
//             Go Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const tenantName = getTenantName();

//   return (
//     <div className="agreement-view-container">
//       {/* Success/Error Message */}
//       {actionMessage && (
//         <div className={`agreement-view-message ${actionMessage.type === 'success' ? 'agreement-view-message-success' : 'agreement-view-message-error'}`}>
//           {actionMessage.text}
//           {actionMessage.type === 'success' && actionSuccess && (
//             <button 
//               className="agreement-view-message-close"
//               onClick={() => setActionMessage(null)}
//             >
//               ×
//             </button>
//           )}
//         </div>
//       )}

//       {/* Agreement Modal - UPDATED with new props */}
//       {showModal && agreementData && (
//         <ViewAgreementModal
//           data={agreementData.agreementjson || agreementData}
//           fileName={`Agreement_${agreementData.AgreementNo || 'Rent'}.pdf`}
//           onClose={handleCloseModal}
//           isVerified={otpVerified}
//           agreementId={getAgreementID()} // NEW: Pass agreement ID
//           onUploadSuccess={handlePdfUploadSuccess} // NEW: Pass callback
//         />
//       )}

//       {/* OTP Modal */}
//       {showOtpModal && (
//         <div className="agreement-otp-overlay">
//           <div className="agreement-otp-modal">
//             <div className="agreement-otp-header">
//               <h3>Verify Your Identity</h3>
//               <button className="agreement-otp-close" onClick={handleCloseOtpModal}>
//                 ×
//               </button>
//             </div>
//             <div className="agreement-otp-body">
//               <p className="agreement-otp-info">
//                 We've sent a 4-digit verification code to <strong>{otpMobile}</strong>
//               </p>
//               <p className="agreement-otp-purpose">
//                 Purpose: {otpPurpose === 'approve' ? 'Approve Agreement' : 'Reject Agreement'}
//               </p>

//               {otpError && (
//                 <div className="agreement-otp-error">{otpError}</div>
//               )}
//               {otpSuccess && (
//                 <div className="agreement-otp-success">{otpSuccess}</div>
//               )}

//               <div className="agreement-otp-input-group">
//                 <input
//                   type="text"
//                   className="agreement-otp-input"
//                   placeholder="Enter 4-digit OTP"
//                   value={otp}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, '');
//                     if (value.length <= 4) {
//                       setOtp(value);
//                     }
//                   }}
//                   maxLength="4"
//                   disabled={isVerifyingOtp || otpVerified}
//                   autoFocus
//                 />
//               </div>

//               <div className="agreement-otp-actions">
//                 <button
//                   className="agreement-otp-btn agreement-otp-btn-verify"
//                   onClick={handleVerifyOtp}
//                   disabled={isVerifyingOtp || otpVerified || otp.length !== 4}
//                 >
//                   {isVerifyingOtp ? 'Verifying...' : otpVerified ? '✓ Verified' : 'Verify OTP'}
//                 </button>
//               </div>

//               <div className="agreement-otp-resend">
//                 <button
//                   className="agreement-otp-btn-resend"
//                   onClick={handleResendOtp}
//                   disabled={isSendingOtp || resendTimer > 0 || otpVerified}
//                 >
//                   {isSendingOtp ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Reject Reason Modal */}
//       {showRejectReasonModal && (
//         <div className="agreement-reject-overlay">
//           <div className="agreement-reject-modal">
//             <div className="agreement-reject-header">
//               <h3>Reason for Rejection</h3>
//               <button 
//                 className="agreement-reject-close" 
//                 onClick={() => setShowRejectReasonModal(false)}
//               >
//                 ×
//               </button>
//             </div>
//             <div className="agreement-reject-body">
//               <p className="agreement-reject-info">
//                 Please provide a reason for rejecting this agreement:
//               </p>
//               {rejectError && (
//                 <div className="agreement-reject-error">{rejectError}</div>
//               )}
//               <textarea
//                 className="agreement-reject-textarea"
//                 placeholder="Enter your reason for rejection..."
//                 value={rejectReason}
//                 onChange={(e) => setRejectReason(e.target.value)}
//                 rows="4"
//                 disabled={isSubmitting}
//               />
//               <div className="agreement-reject-actions">
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-cancel"
//                   onClick={() => setShowRejectReasonModal(false)}
//                   disabled={isSubmitting}
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-submit"
//                   onClick={executeRejectAgreement}
//                   disabled={isSubmitting || !rejectReason.trim()}
//                 >
//                   {isSubmitting ? 'Submitting...' : 'Submit Rejection'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Footer with Checkbox and Buttons */}
//       <div className="agreement-view-footer">
//         <div className="agreement-view-footer-content">
//           {/* Checkbox */}
//           <label className="agreement-view-consent">
//             <input
//               type="checkbox"
//               checked={agreementConsent}
//               onChange={(e) => {
//                 setAgreementConsent(e.target.checked);
//                 if (actionMessage) setActionMessage(null);
//               }}
//               disabled={isSubmitting}
//             />
//             <span>
//               I, <strong>{tenantName}</strong>, have read and agree to the terms of the Rent Agreement above.
//             </span>
//           </label>

//           {/* Action Buttons */}
//           <div className="agreement-view-actions">
//             <button
//               className="agreement-view-btn agreement-view-btn-reject"
//               onClick={handleRejectClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✕ Reject'
//               )}
//             </button>
//             <button
//               className="agreement-view-btn agreement-view-btn-approve"
//               onClick={handleApproveClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✓ Approve'
//               )}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgreementView;











// // src/components/AgreementView.jsx
// import React, { useState, useEffect, useRef } from 'react';
// import { useParams } from 'react-router-dom'; // Remove useSearchParams
// import { 
//   sendAgreementLinkByQuery,
//   sendMsg91Otp,
//   verifyMsg91Otp,
//   generateOtpmsg91,
//   acceptRentAgreement,
//   rejectRentAgreement,
//   verifyOTPAgreement
// } from '../apis/services/pgApi';
// import ViewAgreementModal from './RentAgreementDynamic';
// import '../scss/components/_agreementview.scss';

// // Import the encryption service or create a utility
// // import { decryptAgreementParams } from '../utils/encryption';


// // src/utils/encryption.js
// import * as CryptoJS from 'crypto-js';

// const SECRET_KEY = 'cipl';

// // Generate SHA256 hash of the secret key (matching backend)
// const getSecretKey = () => {
//   return CryptoJS.SHA256(SECRET_KEY).toString();
// };

// // Decrypt agreement parameters
// export const decryptAgreementParams = (encryptedParams) => {
//   try {
//     // Split the encrypted data (IV:encrypted)
//     const parts = encryptedParams.split(':');
//     if (parts.length !== 2) {
//       console.error('Invalid encrypted format');
//       return null;
//     }

//     const [ivHex, encryptedData] = parts;
    
//     // Convert hex strings to WordArrays
//     const iv = CryptoJS.enc.Hex.parse(ivHex);
//     const encrypted = CryptoJS.enc.Hex.parse(encryptedData);

//     // Create decryption key
//     const key = CryptoJS.enc.Hex.parse(getSecretKey());

//     // Decrypt
//     const decrypted = CryptoJS.AES.decrypt(
//       { ciphertext: encrypted },
//       key,
//       {
//         iv: iv,
//         mode: CryptoJS.mode.CBC,
//         padding: CryptoJS.pad.Pkcs7
//       }
//     );

//     const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    
//     if (!decryptedText) {
//       console.error('Decryption failed');
//       return null;
//     }

//     // Parse the decrypted string (format: agreementId|tenantId|pgId)
//     const params = decryptedText.split('|');
//     if (params.length !== 3) {
//       console.error('Invalid decrypted format');
//       return null;
//     }

//     return {
//       agreementId: parseInt(params[0]),
//       tenantId: parseInt(params[1]),
//       pgId: parseInt(params[2])
//     };
//   } catch (error) {
//     console.error('Decryption error:', error);
//     return null;
//   }
// };


// const AgreementView = () => {
//   const { encryptedParams } = useParams(); // Get encrypted params from URL
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [agreementData, setAgreementData] = useState(null);
//   const [showModal, setShowModal] = useState(false);
  
//   // Decrypted parameters
//   const [decryptedParams, setDecryptedParams] = useState(null);
  
//   // Agreement consent states
//   const [agreementConsent, setAgreementConsent] = useState(false);
  
//   // OTP related states
//   const [showOtpModal, setShowOtpModal] = useState(false);
//   const [otp, setOtp] = useState('');
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpId, setOtpId] = useState(null);
//   const [otpPurpose, setOtpPurpose] = useState('');
//   const [otpMobile, setOtpMobile] = useState('');
//   const [otpError, setOtpError] = useState('');
//   const [otpSuccess, setOtpSuccess] = useState('');
//   const [isSendingOtp, setIsSendingOtp] = useState(false);
//   const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
//   const [resendTimer, setResendTimer] = useState(0);
//   const [otpVerified, setOtpVerified] = useState(false);
  
//   // For reject reason
//   const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
//   const [rejectReason, setRejectReason] = useState('');
//   const [rejectError, setRejectError] = useState('');

//   // Action states
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [actionMessage, setActionMessage] = useState(null);
//   const [actionSuccess, setActionSuccess] = useState(false);

//   const timerRef = useRef(null);

//   // Decrypt parameters on component mount
//   useEffect(() => {
//     if (encryptedParams) {
//       const params = decryptAgreementParams(encryptedParams);
//       if (params) {
//         setDecryptedParams(params);
//         console.log('Decrypted params:', params);
//       } else {
//         setError('Invalid or expired agreement link');
//         setLoading(false);
//       }
//     } else {
//       setError('Invalid agreement link');
//       setLoading(false);
//     }
//   }, [encryptedParams]);

//   // Get the tenant name from agreement data
//   const getTenantName = () => {
//     if (!agreementData) return 'Tenant';
//     const data = agreementData.agreementjson || agreementData;
//     const tenantData = data?.tenant || {};
//     return tenantData[0]?.name || tenantData?.Name || 'Tenant';
//   };

//   // Get tenant mobile from agreement data
//   const getTenantMobile = () => {
//     if (!agreementData) return '';
//     const data = agreementData.agreementjson || agreementData;
    
//     if (Array.isArray(data)) {
//       const firstItem = data[0] || {};
//       const tenantData = firstItem?.tenant || {};
//       return tenantData?.mobile || tenantData?.Mobile || '';
//     }
    
//     const tenantData = data?.tenant || {};
//     return tenantData?.mobile || tenantData?.Mobile || '';
//   };

//   // Get agreement ID
//   const getAgreementID = () => {
//     return agreementData?.AgreementID || decryptedParams?.agreementId;
//   };

//   useEffect(() => {
//     const fetchAgreementData = async () => {
//       if (!decryptedParams) return;
      
//       setLoading(true);
//       setError(null);
//       setActionMessage(null);

//       try {
//         const { agreementId, tenantId, pgId } = decryptedParams;
        
//         // Use the IDs from decrypted params
//         const finalPgId = pgId || 8746;
//         const finalTenantId = tenantId || 1025;

//         console.log('Fetching agreement data with decrypted params:', { agreementId, tenantId, pgId });

//         const response = await sendAgreementLinkByQuery(finalPgId, finalTenantId);
        
//         console.log('Agreement response:', response);

//         if (response.success && response.data) {
//           let parsedData = response.data;
//           let agreementJsonData = null;
          
//           // Parse agreementjson if it's a string
//           if (typeof response.data.agreementjson === 'string') {
//             try {
//               const jsonData = JSON.parse(response.data.agreementjson);
//               if (Array.isArray(jsonData) && jsonData.length > 0) {
//                 agreementJsonData = jsonData[0];
//               } else {
//                 agreementJsonData = jsonData;
//               }
//               parsedData = {
//                 ...response.data,
//                 agreementjson: agreementJsonData
//               };
//             } catch (parseError) {
//               console.error('Error parsing agreement JSON:', parseError);
//               parsedData.agreementjson = response.data.agreementjson;
//             }
//           } else if (Array.isArray(response.data.agreementjson)) {
//             agreementJsonData = response.data.agreementjson[0] || response.data.agreementjson;
//             parsedData = {
//               ...response.data,
//               agreementjson: agreementJsonData
//             };
//           } else {
//             agreementJsonData = response.data.agreementjson;
//           }

//           setAgreementData(parsedData);
//           setShowModal(true);
          
//           const mobile = getTenantMobileFromData(parsedData);
//           console.log("Extracted mobile:", mobile);
          
//           if (mobile && mobile.length >= 10) {
//             setOtpMobile(mobile);
//             console.log("OTP Mobile set to:", mobile);
//           } else {
//             console.warn("No valid mobile found in agreement data");
//             setOtpMobile('');
//           }
//         } else {
//           setError(response.message || 'Failed to fetch agreement data');
//         }
//       } catch (err) {
//         console.error('Error fetching agreement:', err);
//         setError(err.message || 'An error occurred while fetching agreement');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAgreementData();
//   }, [decryptedParams]);

//   // Helper function to extract mobile from agreement data
//   const getTenantMobileFromData = (data) => {
//     if (!data) return '';
    
//     const agreementData = data.agreementjson || data;
//     console.log("Agreement data for mobile extraction:", agreementData);
    
//     let sourceData = agreementData;
//     if (Array.isArray(sourceData) && sourceData.length > 0) {
//       sourceData = sourceData[0];
//     }
    
//     let tenantData = null;
    
//     if (sourceData?.tenant) {
//       tenantData = sourceData.tenant;
//     } else if (sourceData?.Tenant) {
//       tenantData = sourceData.Tenant;
//     } else if (sourceData?.tenants) {
//       tenantData = sourceData.tenants;
//     }
    
//     console.log("Tenant data found:", tenantData);
    
//     if (tenantData) {
//       let tenantObject = tenantData;
//       if (Array.isArray(tenantData) && tenantData.length > 0) {
//         tenantObject = tenantData[0];
//         console.log("Tenant data is array, using first element:", tenantObject);
//       }
      
//       const mobile = 
//         tenantObject?.mobile || 
//         tenantObject?.Mobile || 
//         tenantObject?.PhoneNumber || 
//         tenantObject?.phone || 
//         tenantObject?.mobileNumber || 
//         tenantObject?.Phone || 
//         tenantObject?.phoneNumber ||
//         '';
      
//       console.log("Mobile found:", mobile);
      
//       if (mobile && typeof mobile === 'string' && mobile.length >= 10) {
//         return mobile;
//       }
      
//       if (mobile && typeof mobile === 'number') {
//         const mobileStr = mobile.toString();
//         if (mobileStr.length >= 10) {
//           return mobileStr;
//         }
//       }
//     }
    
//     // Search in entire object
//     const allKeys = Object.keys(sourceData);
//     for (const key of allKeys) {
//       const lowerKey = key.toLowerCase();
//       if (lowerKey.includes('mobile') || lowerKey.includes('phone') || lowerKey.includes('contact')) {
//         const value = sourceData[key];
//         if (value) {
//           if (typeof value === 'string') {
//             const cleaned = value.replace(/\D/g, '');
//             if (cleaned.length >= 10) {
//               console.log("Found mobile in key:", key, value);
//               return value;
//             }
//           }
//           if (typeof value === 'number') {
//             const valueStr = value.toString();
//             if (valueStr.length >= 10) {
//               console.log("Found mobile in key:", key, value);
//               return valueStr;
//             }
//           }
//         }
//       }
//     }
    
//     return '';
//   };

//   // Timer for resend OTP
//   useEffect(() => {
//     if (resendTimer > 0) {
//       timerRef.current = setTimeout(() => {
//         setResendTimer(resendTimer - 1);
//       }, 1000);
//     }
//     return () => {
//       if (timerRef.current) {
//         clearTimeout(timerRef.current);
//       }
//     };
//   }, [resendTimer]);

//   const handleCloseModal = () => {
//     setShowModal(false);
//   };

//   // Handle sending OTP
//   const handleSendOtp = async (purpose) => {
//     if (!agreementConsent) {
//       setActionMessage({
//         type: 'error',
//         text: 'Please accept the terms and conditions before proceeding.'
//       });
//       return;
//     }

//     const mobileNumber = otpMobile || getTenantMobile();
//     if (!mobileNumber || mobileNumber.length < 10) {
//       setOtpError('Invalid mobile number. Please contact support.');
//       return;
//     }

//     if (!otpMobile && mobileNumber) {
//       setOtpMobile(mobileNumber);
//     }

//     setOtpPurpose(purpose);
//     setIsSendingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtpVerified(false);
//     setOtp('');
//     setOtpId(null);

//     try {
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: `91${mobileNumber}`,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       console.log('Send OTP Response:', sendOtpResponse);

//       if (sendOtpResponse.success) {
//         setOtpSent(true);
//         setOtpSuccess('OTP sent successfully to your mobile number!');
//         setResendTimer(30);
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to send OTP');
//       }
//     } catch (err) {
//       console.error('Error sending OTP:', err);
//       setOtpError(err.message || 'An error occurred while sending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Handle verifying OTP
//   const handleVerifyOtp = async () => {
//     if (!otp || otp.length !== 4) {
//       setOtpError('Please enter a valid 4-digit OTP');
//       return;
//     }

//     setIsVerifyingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');

//     try {
//       const verifyResponse = await verifyMsg91Otp(`91${otpMobile}`, otp);
//       console.log('Verify OTP Response:', verifyResponse);

//       if (verifyResponse.success) {
//         const generateResponse = await generateOtpmsg91(
//           otpMobile,
//           'RentAgreement',
//           otp,
//           decryptedParams?.pgId || 8746,
//           getAgreementID()
//         );

//         console.log('Generate OTP in DB Response:', generateResponse);

//         if (generateResponse.success) {
//           const verifyPayload = {
//             MobileNumber: otpMobile,
//             OTPCode: otp,
//             Purpose: 'RentAgreement',
//             FullName: '',
//             Email: '',
//             ReferenceId: parseInt(getAgreementID())
//           };
//           const verifyResponse = await verifyOTPAgreement(verifyPayload);

//           if (verifyResponse.Success == 1) {
//             console.log('OTP verified with ID:', otpId);
//             console.log("otpid", generateResponse.data?.otpId);
//             setOtpId(generateResponse.data?.otpId);
//             setOtpVerified(true);
//             setOtpSuccess('OTP verified successfully!');


//               // Show OTP verification success alert
//         //   alert('✅ OTP Verified Successfully!\n\nYour identity has been verified. You can now proceed with the agreement action.');


//             setTimeout(() => {
//               setShowOtpModal(false);
//               if (otpPurpose === 'approve') {
//                 executeApproveAgreement(generateResponse.data?.otpId);
//               } else if (otpPurpose === 'reject') {
//                 setShowRejectReasonModal(true);
//               }
//             }, 1000);
//           }
//         } else {
//           setOtpError(generateResponse.message || 'Failed to save OTP verification');
//         }
//       } else {
//         setOtpError(verifyResponse.message || 'Invalid OTP. Please try again.');
//       }
//     } catch (err) {
//       console.error('Error verifying OTP:', err);
//       setOtpError(err.message || 'An error occurred while verifying OTP');
//     } finally {
//       setIsVerifyingOtp(false);
//     }
//   };

//   // Handle resend OTP
//   const handleResendOtp = async () => {
//     if (resendTimer > 0) return;
    
//     setOtpError('');
//     setOtpSuccess('');
//     setIsSendingOtp(true);

//     try {
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: `91${otpMobile}`,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       if (sendOtpResponse.success) {
//         setOtpSuccess('OTP resent successfully!');
//         setResendTimer(30);
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to resend OTP');
//       }
//     } catch (err) {
//       console.error('Error resending OTP:', err);
//       setOtpError(err.message || 'An error occurred while resending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Execute Approve Agreement
//   const executeApproveAgreement = async (otpid) => {
//     console.log("generateResponse.data?.otpId approve", otpid);
//     setIsSubmitting(true);
//     setActionMessage(null);

//     try {
//       const agreementID = getAgreementID();
      
//       const acceptData = {
//         AgreementID: parseInt(agreementID),
//         OTPID: parseInt(otpid),
//         IPAddress: '192.168.1.100',
//         DeviceInfo: navigator.userAgent || 'Chrome Browser',
//         Latitude: "28.613939",
//         Longitude: "77.209021"
//       };

//       console.log('Accepting agreement with data:', acceptData);

//       const response = await acceptRentAgreement(acceptData);

//       console.log('Accept Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(true);
//         setActionMessage({
//           type: 'success',
//           text: '🎉 Agreement accepted successfully! You can now download the signed agreement.'
//         });


//           // Show success alert
//         //   alert('✅ Agreement Approved Successfully!\n\nYou have successfully approved the rent agreement. The agreement has been signed digitally and is now final.');

//           // Set verified state to true to show signature in PDF
//           setOtpVerified(true);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to accept agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error accepting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while accepting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Execute Reject Agreement
//   const executeRejectAgreement = async () => {
//     console.log("reject otpid", otpId);
//     if (!rejectReason || rejectReason.trim().length === 0) {
//       setRejectError('Please provide a reason for rejection');
//       return;
//     }

//     setIsSubmitting(true);
//     setActionMessage(null);
//     setRejectError('');

//     try {
//       const agreementID = getAgreementID();
      
//       const rejectData = {
//         AgreementID: parseInt(agreementID),
//         RejectReason: rejectReason.trim(),
//         IPAddress: '192.168.1.100',
//         DeviceInfo: navigator.userAgent || 'Chrome Browser'
//       };

//       console.log('Rejecting agreement with data:', rejectData);

//       const response = await rejectRentAgreement(rejectData);

//       console.log('Reject Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(false);
//         setActionMessage({
//           type: 'error',
//           text: '❌ Agreement rejected. You can contact the landlord for further discussion.'
//         });

//         // Show rejection alert
//     //   alert('❌ Agreement Rejected\n\nYou have rejected the rent agreement. The landlord will be notified of your decision.');

//         setShowRejectReasonModal(false);
//         setTimeout(() => handleCloseModal(), 3000);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to reject agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error rejecting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while rejecting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Handle Approve button click
//   const handleApproveClick = () => {
//     if (!otpMobile) {
//       const mobile = getTenantMobile();
//       if (mobile) {
//         setOtpMobile(mobile);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: 'No mobile number found for this tenant. Please contact support.'
//         });
//         return;
//       }
//     }
    
//     setShowOtpModal(true);
//     setOtpPurpose('approve');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//     setTimeout(() => {
//       handleSendOtp('approve');
//     }, 500);
//   };

//   // Handle Reject button click
//   const handleRejectClick = () => {
//     if (!otpMobile) {
//       const mobile = getTenantMobile();
//       if (mobile) {
//         setOtpMobile(mobile);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: 'No mobile number found for this tenant. Please contact support.'
//         });
//         return;
//       }
//     }
    
//     setShowOtpModal(true);
//     setOtpPurpose('reject');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//     setTimeout(() => {
//       handleSendOtp('reject');
//     }, 500);
//   };

//   // Close OTP modal
//   const handleCloseOtpModal = () => {
//     setShowOtpModal(false);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//   };

//   if (loading) {
//     return (
//       <div className="agreement-view-loading">
//         <div className="agreement-view-spinner"></div>
//         <p>Loading your agreement...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="agreement-view-error">
//         <div className="agreement-view-error-content">
//           <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
//             <circle cx="12" cy="12" r="10" />
//             <line x1="12" y1="8" x2="12" y2="12" />
//             <line x1="12" y1="16" x2="12.01" y2="16" />
//           </svg>
//           <h2>Unable to Load Agreement</h2>
//           <p>{error}</p>
//           <button 
//             className="agreement-view-retry-btn"
//             onClick={() => window.location.reload()}
//           >
//             Retry
//           </button>
//           <button 
//             className="agreement-view-home-btn"
//             onClick={() => window.location.href = '/'}
//           >
//             Go Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const tenantName = getTenantName();

//   return (
//     <div className="agreement-view-container">
//       {/* Success/Error Message */}
//       {actionMessage && (
//         <div className={`agreement-view-message ${actionMessage.type === 'success' ? 'agreement-view-message-success' : 'agreement-view-message-error'}`}>
//           {actionMessage.text}
//           {actionMessage.type === 'success' && actionSuccess && (
//             <button 
//               className="agreement-view-message-close"
//               onClick={() => setActionMessage(null)}
//             >
//               ×
//             </button>
//           )}
//         </div>
//       )}

//       {/* Agreement Modal */}
//       {showModal && agreementData && (
//         <ViewAgreementModal
//           data={agreementData.agreementjson || agreementData}
//           fileName={`Agreement_${agreementData.AgreementNo || 'Rent'}.pdf`}
//           onClose={handleCloseModal}
//           isVerified={otpVerified}
//         />
//       )}

//       {/* OTP Modal */}
//       {showOtpModal && (
//         <div className="agreement-otp-overlay">
//           <div className="agreement-otp-modal">
//             <div className="agreement-otp-header">
//               <h3>Verify Your Identity</h3>
//               <button className="agreement-otp-close" onClick={handleCloseOtpModal}>
//                 ×
//               </button>
//             </div>
//             <div className="agreement-otp-body">
//               <p className="agreement-otp-info">
//                 We've sent a 4-digit verification code to <strong>{otpMobile}</strong>
//               </p>
//               <p className="agreement-otp-purpose">
//                 Purpose: {otpPurpose === 'approve' ? 'Approve Agreement' : 'Reject Agreement'}
//               </p>

//               {otpError && (
//                 <div className="agreement-otp-error">{otpError}</div>
//               )}
//               {otpSuccess && (
//                 <div className="agreement-otp-success">{otpSuccess}</div>
//               )}

//               <div className="agreement-otp-input-group">
//                 <input
//                   type="text"
//                   className="agreement-otp-input"
//                   placeholder="Enter 4-digit OTP"
//                   value={otp}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, '');
//                     if (value.length <= 4) {
//                       setOtp(value);
//                     }
//                   }}
//                   maxLength="4"
//                   disabled={isVerifyingOtp || otpVerified}
//                   autoFocus
//                 />
//               </div>

//               <div className="agreement-otp-actions">
//                 <button
//                   className="agreement-otp-btn agreement-otp-btn-verify"
//                   onClick={handleVerifyOtp}
//                   disabled={isVerifyingOtp || otpVerified || otp.length !== 4}
//                 >
//                   {isVerifyingOtp ? 'Verifying...' : otpVerified ? '✓ Verified' : 'Verify OTP'}
//                 </button>
//               </div>

//               <div className="agreement-otp-resend">
//                 <button
//                   className="agreement-otp-btn-resend"
//                   onClick={handleResendOtp}
//                   disabled={isSendingOtp || resendTimer > 0 || otpVerified}
//                 >
//                   {isSendingOtp ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Reject Reason Modal */}
//       {showRejectReasonModal && (
//         <div className="agreement-reject-overlay">
//           <div className="agreement-reject-modal">
//             <div className="agreement-reject-header">
//               <h3>Reason for Rejection</h3>
//               <button 
//                 className="agreement-reject-close" 
//                 onClick={() => setShowRejectReasonModal(false)}
//               >
//                 ×
//               </button>
//             </div>
//             <div className="agreement-reject-body">
//               <p className="agreement-reject-info">
//                 Please provide a reason for rejecting this agreement:
//               </p>
//               {rejectError && (
//                 <div className="agreement-reject-error">{rejectError}</div>
//               )}
//               <textarea
//                 className="agreement-reject-textarea"
//                 placeholder="Enter your reason for rejection..."
//                 value={rejectReason}
//                 onChange={(e) => setRejectReason(e.target.value)}
//                 rows="4"
//                 disabled={isSubmitting}
//               />
//               <div className="agreement-reject-actions">
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-cancel"
//                   onClick={() => setShowRejectReasonModal(false)}
//                   disabled={isSubmitting}
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-submit"
//                   onClick={executeRejectAgreement}
//                   disabled={isSubmitting || !rejectReason.trim()}
//                 >
//                   {isSubmitting ? 'Submitting...' : 'Submit Rejection'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Footer with Checkbox and Buttons */}
//       <div className="agreement-view-footer">
//         <div className="agreement-view-footer-content">
//           {/* Checkbox */}
//           <label className="agreement-view-consent">
//             <input
//               type="checkbox"
//               checked={agreementConsent}
//               onChange={(e) => {
//                 setAgreementConsent(e.target.checked);
//                 if (actionMessage) setActionMessage(null);
//               }}
//               disabled={isSubmitting}
//             />
//             <span>
//               I, <strong>{tenantName}</strong>, have read and agree to the terms of the Rent Agreement above.
//             </span>
//           </label>

//           {/* Action Buttons */}
//           <div className="agreement-view-actions">
//             <button
//               className="agreement-view-btn agreement-view-btn-reject"
//               onClick={handleRejectClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✕ Reject'
//               )}
//             </button>
//             <button
//               className="agreement-view-btn agreement-view-btn-approve"
//               onClick={handleApproveClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✓ Approve'
//               )}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgreementView;






// // src/components/AgreementView.jsx

// import React, { useState, useEffect, useRef } from 'react';
// import { useParams, useSearchParams } from 'react-router-dom';
// import { 
//   sendAgreementLinkByQuery,
//   sendMsg91Otp,
//   verifyMsg91Otp,
//   generateOtpmsg91,
//   acceptRentAgreement,
//   rejectRentAgreement,
//   verifyOTPAgreement
// } from '../apis/services/pgApi';
// import ViewAgreementModal from './RentAgreementDynamic';
// import '../scss/components/_agreementview.scss';

// const AgreementView = () => {
//   const { agreementId } = useParams();
//   const [searchParams] = useSearchParams();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [agreementData, setAgreementData] = useState(null);
//   const [showModal, setShowModal] = useState(false);
  
//   // Agreement consent states
//   const [agreementConsent, setAgreementConsent] = useState(false);
  
//   // OTP related states
//   const [showOtpModal, setShowOtpModal] = useState(false);
//   const [otp, setOtp] = useState('');
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpId, setOtpId] = useState(null);
//   const [otpPurpose, setOtpPurpose] = useState(''); // 'approve' or 'reject'
//   const [otpMobile, setOtpMobile] = useState('');
//   const [otpError, setOtpError] = useState('');
//   const [otpSuccess, setOtpSuccess] = useState('');
//   const [isSendingOtp, setIsSendingOtp] = useState(false);
//   const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
//   const [resendTimer, setResendTimer] = useState(0);
//   const [otpVerified, setOtpVerified] = useState(false);
  
//   // For reject reason
//   const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
//   const [rejectReason, setRejectReason] = useState('');
//   const [rejectError, setRejectError] = useState('');

//   // Action states
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [actionMessage, setActionMessage] = useState(null);
//   const [actionSuccess, setActionSuccess] = useState(false);

//   const timerRef = useRef(null);

//   // Get tenantId and pgId from URL params
//   const tenantId = searchParams.get('tenant');
//   const pgId = searchParams.get('pg');

//   // Get the tenant name from agreement data
//   const getTenantName = () => {
//     if (!agreementData) return 'Tenant';
//     const data = agreementData.agreementjson || agreementData;
//     // Handle both object and array formats
//     const tenantData = data?.tenant || {};
//     return tenantData?.name || tenantData?.Name || 'Tenant';
//   };

//   // Get tenant mobile from agreement data - FIXED
//   const getTenantMobile = () => {
//     if (!agreementData) return '';
//     const data = agreementData.agreementjson || agreementData;
    
//     // Handle array format from API
//     if (Array.isArray(data)) {
//       const firstItem = data[0] || {};
//       const tenantData = firstItem?.tenant || {};
//       return tenantData?.mobile || tenantData?.Mobile || '';
//     }
    
//     // Handle object format
//     const tenantData = data?.tenant || {};
//     return tenantData?.mobile || tenantData?.Mobile || '';
//   };

//   // Get agreement ID
//   const getAgreementID = () => {
//     return agreementData?.AgreementID || agreementId;
//   };

//   useEffect(() => {
//     const fetchAgreementData = async () => {
//       setLoading(true);
//       setError(null);
//       setActionMessage(null);

//       try {
//         const finalPgId = pgId || 8746;
//         const finalTenantId = tenantId || 1025;

//         console.log('Fetching agreement data for PG:', finalPgId, 'Tenant:', finalTenantId);

//         const response = await sendAgreementLinkByQuery(finalPgId, finalTenantId);
        
//         console.log('Agreement response:', response);

//         if (response.success && response.data) {
//           let parsedData = response.data;
//           let agreementJsonData = null;
          
//           // Parse agreementjson if it's a string
//           if (typeof response.data.agreementjson === 'string') {
//             try {
//               const jsonData = JSON.parse(response.data.agreementjson);
//               // Check if jsonData is an array or object
//               if (Array.isArray(jsonData) && jsonData.length > 0) {
//                 agreementJsonData = jsonData[0];
//               } else {
//                 agreementJsonData = jsonData;
//               }
//               parsedData = {
//                 ...response.data,
//                 agreementjson: agreementJsonData
//               };
//             } catch (parseError) {
//               console.error('Error parsing agreement JSON:', parseError);
//               parsedData.agreementjson = response.data.agreementjson;
//             }
//           } else if (Array.isArray(response.data.agreementjson)) {
//             agreementJsonData = response.data.agreementjson[0] || response.data.agreementjson;
//             parsedData = {
//               ...response.data,
//               agreementjson: agreementJsonData
//             };
//           } else {
//             agreementJsonData = response.data.agreementjson;
//           }

//           setAgreementData(parsedData);
//           setShowModal(true);
          
//           // Set mobile number from tenant data - using the fixed function
//           const mobile = getTenantMobileFromData(parsedData);
//           console.log("Extracted mobile:", mobile);
          
//           if (mobile && mobile.length >= 10) {
//             setOtpMobile(mobile);
//             console.log("OTP Mobile set to:", mobile);
//           } else {
//             console.warn("No valid mobile found in agreement data");
//             // Set a fallback or show error
//             setOtpMobile(''); // Empty will show error on OTP send
//           }
//         } else {
//           setError(response.message || 'Failed to fetch agreement data');
//         }
//       } catch (err) {
//         console.error('Error fetching agreement:', err);
//         setError(err.message || 'An error occurred while fetching agreement');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAgreementData();
//   }, [agreementId, tenantId, pgId]);

//   // Helper function to extract mobile from agreement data
// //   const getTenantMobileFromData = (data) => {
// //     if (!data) return '';
    
// //     // First check if we have agreementjson object
// //     const agreementData = data.agreementjson || data;
    
// //     console.log("Agreement data for mobile extraction:", agreementData);
    
// //     // If agreementData is an array, get first item
// //     const sourceData = Array.isArray(agreementData) ? agreementData[0] : agreementData;
    
// //     // Try to get tenant from various possible paths
// //     let tenantData = null;
    
// //     if (sourceData?.tenant) {
// //       tenantData = sourceData.tenant;
// //     } else if (sourceData?.Tenant) {
// //       tenantData = sourceData.Tenant;
// //     }
    
// //     console.log("Tenant data found:", tenantData);
    
// //     if (tenantData) {
// //       // Try different possible field names for mobile
// //       const mobile = tenantData[0].mobile || tenantData.Mobile || 
// //                      tenantData.PhoneNumber || tenantData.phone || 
// //                      tenantData.mobileNumber || '';
// //       console.log("Mobile found:", mobile);
// //       return mobile;
// //     }
    
// //     // If tenant not found, try to search in the entire object
// //     const allKeys = Object.keys(sourceData);
// //     for (const key of allKeys) {
// //       if (key.toLowerCase().includes('mobile') || key.toLowerCase().includes('phone')) {
// //         const value = sourceData[key];
// //         if (value && typeof value === 'string' && value.length >= 10) {
// //           console.log("Found mobile in key:", key, value);
// //           return value;
// //         }
// //       }
// //     }
    
// //     return '';
// //   };

// const getTenantMobileFromData = (data) => {
//   if (!data) return '';
  
//   // First check if we have agreementjson object
//   const agreementData = data.agreementjson || data;
  
//   console.log("Agreement data for mobile extraction:", agreementData);
  
//   // If agreementData is an array, get first item
//   let sourceData = agreementData;
//   if (Array.isArray(sourceData) && sourceData.length > 0) {
//     sourceData = sourceData[0];
//   }
  
//   // Try to get tenant from various possible paths
//   let tenantData = null;
  
//   if (sourceData?.tenant) {
//     tenantData = sourceData.tenant;
//   } else if (sourceData?.Tenant) {
//     tenantData = sourceData.Tenant;
//   } else if (sourceData?.tenants) {
//     tenantData = sourceData.tenants;
//   }
  
//   console.log("Tenant data found:", tenantData);
  
//   if (tenantData) {
//     // Check if tenantData is an array
//     let tenantObject = tenantData;
//     if (Array.isArray(tenantData) && tenantData.length > 0) {
//       tenantObject = tenantData[0];
//       console.log("Tenant data is array, using first element:", tenantObject);
//     }
    
//     // Try different possible field names for mobile
//     const mobile = 
//       tenantObject?.mobile || 
//       tenantObject?.Mobile || 
//       tenantObject?.PhoneNumber || 
//       tenantObject?.phone || 
//       tenantObject?.mobileNumber || 
//       tenantObject?.Phone || 
//       tenantObject?.phoneNumber ||
//       '';
    
//     console.log("Mobile found:", mobile);
    
//     // If mobile is found and is a valid string, return it
//     if (mobile && typeof mobile === 'string' && mobile.length >= 10) {
//       return mobile;
//     }
    
//     // If mobile is a number, convert to string
//     if (mobile && typeof mobile === 'number') {
//       const mobileStr = mobile.toString();
//       if (mobileStr.length >= 10) {
//         return mobileStr;
//       }
//     }
//   }
  
//   // If tenant not found, try to search in the entire object
//   const allKeys = Object.keys(sourceData);
//   for (const key of allKeys) {
//     const lowerKey = key.toLowerCase();
//     if (lowerKey.includes('mobile') || lowerKey.includes('phone') || lowerKey.includes('contact')) {
//       const value = sourceData[key];
//       if (value) {
//         // If value is a string and has at least 10 digits
//         if (typeof value === 'string') {
//           const cleaned = value.replace(/\D/g, '');
//           if (cleaned.length >= 10) {
//             console.log("Found mobile in key:", key, value);
//             return value;
//           }
//         }
//         // If value is a number
//         if (typeof value === 'number') {
//           const valueStr = value.toString();
//           if (valueStr.length >= 10) {
//             console.log("Found mobile in key:", key, value);
//             return valueStr;
//           }
//         }
//       }
//     }
//   }
  
//   // If still not found, try to search inside nested objects
//   for (const key of allKeys) {
//     const value = sourceData[key];
//     if (value && typeof value === 'object' && !Array.isArray(value)) {
//       const nestedKeys = Object.keys(value);
//       for (const nestedKey of nestedKeys) {
//         const lowerNestedKey = nestedKey.toLowerCase();
//         if (lowerNestedKey.includes('mobile') || lowerNestedKey.includes('phone')) {
//           const nestedValue = value[nestedKey];
//           if (nestedValue && typeof nestedValue === 'string' && nestedValue.length >= 10) {
//             console.log("Found mobile in nested key:", nestedKey, nestedValue);
//             return nestedValue;
//           }
//         }
//       }
//     }
//   }
  
//   return '';
// };

//   // Timer for resend OTP
//   useEffect(() => {
//     if (resendTimer > 0) {
//       timerRef.current = setTimeout(() => {
//         setResendTimer(resendTimer - 1);
//       }, 1000);
//     }
//     return () => {
//       if (timerRef.current) {
//         clearTimeout(timerRef.current);
//       }
//     };
//   }, [resendTimer]);

//   const handleCloseModal = () => {
//     setShowModal(false);
//   };

//   // Handle sending OTP - Only sends MSG91 OTP, does NOT generate in DB
//   const handleSendOtp = async (purpose) => {
//     // Validate consent
//     if (!agreementConsent) {
//       setActionMessage({
//         type: 'error',
//         text: 'Please accept the terms and conditions before proceeding.'
//       });
//       return;
//     }

//     // Validate mobile
//     const mobileNumber = otpMobile || getTenantMobile();
//     if (!mobileNumber || mobileNumber.length < 10) {
//       setOtpError('Invalid mobile number. Please contact support.');
//       return;
//     }

//     // Set the mobile if it was empty
//     if (!otpMobile && mobileNumber) {
//       setOtpMobile(mobileNumber);
//     }

//     setOtpPurpose(purpose);
//     setIsSendingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtpVerified(false);
//     setOtp('');
//     setOtpId(null);

//     try {
//       // Step 1: Send OTP via MSG91 only - No DB generation yet
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: `91${mobileNumber}`,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       console.log('Send OTP Response:', sendOtpResponse);

//       if (sendOtpResponse.success) {
//         setOtpSent(true);
//         setOtpSuccess('OTP sent successfully to your mobile number!');
//         setResendTimer(30); // 30 seconds cooldown
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to send OTP');
//       }
//     } catch (err) {
//       console.error('Error sending OTP:', err);
//       setOtpError(err.message || 'An error occurred while sending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Handle verifying OTP
//     const handleVerifyOtp = async () => {
//         // Validate OTP length (4 digits)
//         if (!otp || otp.length !== 4) {
//             setOtpError('Please enter a valid 4-digit OTP');
//             return;
//         }

//         setIsVerifyingOtp(true);
//         setOtpError('');
//         setOtpSuccess('');

//         try {
//             // Step 1: Verify OTP via MSG91
//             const verifyResponse = await verifyMsg91Otp(`91${otpMobile}`, otp);

//             console.log('Verify OTP Response:', verifyResponse);

//             if (verifyResponse.success) {
//                 // Step 2: ONLY AFTER successful verification, generate OTP in database
//                 // with the OTP that the user entered and was verified
//                 const generateResponse = await generateOtpmsg91(
//                     otpMobile,
//                     'RentAgreement',
//                     otp, // The OTP that user entered and was verified
//                     pgId || 8746,
//                     getAgreementID()
//                 );

//                 console.log('Generate OTP in DB Response:', generateResponse);

//                 if (generateResponse.success) {

//                     const verifyPayload = {
//                         MobileNumber: otpMobile,
//                         OTPCode: otp,  // The OTP user entered
//                         Purpose: 'RentAgreement',
//                         FullName: '',
//                         Email: '',
//                         ReferenceId: parseInt(agreementId)
//                     };
//                     const verifyResponse = await verifyOTPAgreement(verifyPayload);

//                     if (verifyResponse.Success==1) {
//                         // OTP verified successfully
//                         console.log('OTP verified with ID:', otpId);

//                         console.log("otpid", generateResponse.data?.otpId)
//                     setOtpId(generateResponse.data?.otpId);
//                     setOtpVerified(true);
//                     setOtpSuccess('OTP verified successfully!');

//                     // Close OTP modal after success
//                     setTimeout(() => {
//                         setShowOtpModal(false);
//                         // Execute the approve or reject action
//                         if (otpPurpose === 'approve') {
//                             executeApproveAgreement(generateResponse.data?.otpId);
//                         } else if (otpPurpose === 'reject') {
//                             setShowRejectReasonModal(true);
//                         }
//                     }, 1000);
//                     }


                    
//                 } else {
//                     setOtpError(generateResponse.message || 'Failed to save OTP verification');
//                 }
//             } else {
//                 setOtpError(verifyResponse.message || 'Invalid OTP. Please try again.');
//             }
//         } catch (err) {
//             console.error('Error verifying OTP:', err);
//             setOtpError(err.message || 'An error occurred while verifying OTP');
//         } finally {
//             setIsVerifyingOtp(false);
//         }
//     };

//   // Handle resend OTP
//   const handleResendOtp = async () => {
//     if (resendTimer > 0) return;
    
//     setOtpError('');
//     setOtpSuccess('');
//     setIsSendingOtp(true);

//     try {
//       // Only resend MSG91 OTP - DB generation will happen after verification
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: `91${otpMobile}`,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       if (sendOtpResponse.success) {
//         setOtpSuccess('OTP resent successfully!');
//         setResendTimer(30);
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to resend OTP');
//       }
//     } catch (err) {
//       console.error('Error resending OTP:', err);
//       setOtpError(err.message || 'An error occurred while resending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Execute Approve Agreement
//   const executeApproveAgreement = async (otpid) => {

//     console.log("generateResponse.data?.otpId approve", otpid)
//     setIsSubmitting(true);
//     setActionMessage(null);

//     try {
//       const agreementID = getAgreementID();
      
//       const acceptData = {
//         AgreementID: parseInt(agreementID),
//         OTPID: parseInt(otpid),
//         IPAddress: '192.168.1.100', // Hardcoded for now
//         DeviceInfo: navigator.userAgent || 'Chrome Browser',
//         Latitude: "28.613939", // Hardcoded for now
//         Longitude: "77.209021" // Hardcoded for now
//       };

//       console.log('Accepting agreement with data:', acceptData);

//       const response = await acceptRentAgreement(acceptData);

//       console.log('Accept Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(true);
//         setActionMessage({
//           type: 'success',
//           text: '🎉 Agreement accepted successfully! You can now download the signed agreement.'
//         });
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to accept agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error accepting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while accepting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Execute Reject Agreement
//   const executeRejectAgreement = async () => {
//     console.log("reject otpid", otpId)
//     if (!rejectReason || rejectReason.trim().length === 0) {
//       setRejectError('Please provide a reason for rejection');
//       return;
//     }

//     setIsSubmitting(true);
//     setActionMessage(null);
//     setRejectError('');

//     try {
//       const agreementID = getAgreementID();
      
//       const rejectData = {
//         AgreementID: parseInt(agreementID),
//         RejectReason: rejectReason.trim(),
//         IPAddress: '192.168.1.100', // Hardcoded for now
//         DeviceInfo: navigator.userAgent || 'Chrome Browser'
//       };

//       console.log('Rejecting agreement with data:', rejectData);

//       const response = await rejectRentAgreement(rejectData);

//       console.log('Reject Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(false);
//         setActionMessage({
//           type: 'error',
//           text: '❌ Agreement rejected. You can contact the landlord for further discussion.'
//         });
//         setShowRejectReasonModal(false);
//         // Close modal after showing message
//         setTimeout(() => handleCloseModal(), 3000);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to reject agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error rejecting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while rejecting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Handle Approve button click - Show OTP modal
//   const handleApproveClick = () => {
//     // Ensure we have mobile number
//     if (!otpMobile) {
//       const mobile = getTenantMobile();
//       if (mobile) {
//         setOtpMobile(mobile);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: 'No mobile number found for this tenant. Please contact support.'
//         });
//         return;
//       }
//     }
    
//     setShowOtpModal(true);
//     setOtpPurpose('approve');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//     // Auto send OTP when modal opens
//     setTimeout(() => {
//       handleSendOtp('approve');
//     }, 500);
//   };

//   // Handle Reject button click - Show OTP modal first
//   const handleRejectClick = () => {
//     // Ensure we have mobile number
//     if (!otpMobile) {
//       const mobile = getTenantMobile();
//       if (mobile) {
//         setOtpMobile(mobile);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: 'No mobile number found for this tenant. Please contact support.'
//         });
//         return;
//       }
//     }
    
//     setShowOtpModal(true);
//     setOtpPurpose('reject');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//     // Auto send OTP when modal opens
//     setTimeout(() => {
//       handleSendOtp('reject');
//     }, 500);
//   };

//   // Close OTP modal
//   const handleCloseOtpModal = () => {
//     setShowOtpModal(false);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//   };

//   if (loading) {
//     return (
//       <div className="agreement-view-loading">
//         <div className="agreement-view-spinner"></div>
//         <p>Loading your agreement...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="agreement-view-error">
//         <div className="agreement-view-error-content">
//           <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
//             <circle cx="12" cy="12" r="10" />
//             <line x1="12" y1="8" x2="12" y2="12" />
//             <line x1="12" y1="16" x2="12.01" y2="16" />
//           </svg>
//           <h2>Unable to Load Agreement</h2>
//           <p>{error}</p>
//           <button 
//             className="agreement-view-retry-btn"
//             onClick={() => window.location.reload()}
//           >
//             Retry
//           </button>
//           <button 
//             className="agreement-view-home-btn"
//             onClick={() => window.location.href = '/'}
//           >
//             Go Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const tenantName = getTenantName();

//   return (
//     <div className="agreement-view-container">
//       {/* Success/Error Message */}
//       {actionMessage && (
//         <div className={`agreement-view-message ${actionMessage.type === 'success' ? 'agreement-view-message-success' : 'agreement-view-message-error'}`}>
//           {actionMessage.text}
//           {actionMessage.type === 'success' && actionSuccess && (
//             <button 
//               className="agreement-view-message-close"
//               onClick={() => setActionMessage(null)}
//             >
//               ×
//             </button>
//           )}
//         </div>
//       )}

//       {/* Agreement Modal */}
//       {showModal && agreementData && (
//         <ViewAgreementModal
//           data={agreementData.agreementjson || agreementData}
//           fileName={`Agreement_${agreementData.AgreementNo || 'Rent'}.pdf`}
//           onClose={handleCloseModal}
//         />
//       )}

//       {/* OTP Modal */}
//       {showOtpModal && (
//         <div className="agreement-otp-overlay">
//           <div className="agreement-otp-modal">
//             <div className="agreement-otp-header">
//               <h3>Verify Your Identity</h3>
//               <button className="agreement-otp-close" onClick={handleCloseOtpModal}>
//                 ×
//               </button>
//             </div>
//             <div className="agreement-otp-body">
//               <p className="agreement-otp-info">
//                 We've sent a 4-digit verification code to <strong>{otpMobile}</strong>
//               </p>
//               <p className="agreement-otp-purpose">
//                 Purpose: {otpPurpose === 'approve' ? 'Approve Agreement' : 'Reject Agreement'}
//               </p>

//               {otpError && (
//                 <div className="agreement-otp-error">{otpError}</div>
//               )}
//               {otpSuccess && (
//                 <div className="agreement-otp-success">{otpSuccess}</div>
//               )}

//               <div className="agreement-otp-input-group">
//                 <input
//                   type="text"
//                   className="agreement-otp-input"
//                   placeholder="Enter 4-digit OTP"
//                   value={otp}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, '');
//                     if (value.length <= 4) {
//                       setOtp(value);
//                     }
//                   }}
//                   maxLength="4"
//                   disabled={isVerifyingOtp || otpVerified}
//                   autoFocus
//                 />
//               </div>

//               <div className="agreement-otp-actions">
//                 <button
//                   className="agreement-otp-btn agreement-otp-btn-verify"
//                   onClick={handleVerifyOtp}
//                   disabled={isVerifyingOtp || otpVerified || otp.length !== 4}
//                 >
//                   {isVerifyingOtp ? 'Verifying...' : otpVerified ? '✓ Verified' : 'Verify OTP'}
//                 </button>
//               </div>

//               <div className="agreement-otp-resend">
//                 <button
//                   className="agreement-otp-btn-resend"
//                   onClick={handleResendOtp}
//                   disabled={isSendingOtp || resendTimer > 0 || otpVerified}
//                 >
//                   {isSendingOtp ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Reject Reason Modal */}
//       {showRejectReasonModal && (
//         <div className="agreement-reject-overlay">
//           <div className="agreement-reject-modal">
//             <div className="agreement-reject-header">
//               <h3>Reason for Rejection</h3>
//               <button 
//                 className="agreement-reject-close" 
//                 onClick={() => setShowRejectReasonModal(false)}
//               >
//                 ×
//               </button>
//             </div>
//             <div className="agreement-reject-body">
//               <p className="agreement-reject-info">
//                 Please provide a reason for rejecting this agreement:
//               </p>
//               {rejectError && (
//                 <div className="agreement-reject-error">{rejectError}</div>
//               )}
//               <textarea
//                 className="agreement-reject-textarea"
//                 placeholder="Enter your reason for rejection..."
//                 value={rejectReason}
//                 onChange={(e) => setRejectReason(e.target.value)}
//                 rows="4"
//                 disabled={isSubmitting}
//               />
//               <div className="agreement-reject-actions">
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-cancel"
//                   onClick={() => setShowRejectReasonModal(false)}
//                   disabled={isSubmitting}
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-submit"
//                   onClick={executeRejectAgreement}
//                   disabled={isSubmitting || !rejectReason.trim()}
//                 >
//                   {isSubmitting ? 'Submitting...' : 'Submit Rejection'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Footer with Checkbox and Buttons */}
//       <div className="agreement-view-footer">
//         <div className="agreement-view-footer-content">
//           {/* Checkbox */}
//           <label className="agreement-view-consent">
//             <input
//               type="checkbox"
//               checked={agreementConsent}
//               onChange={(e) => {
//                 setAgreementConsent(e.target.checked);
//                 if (actionMessage) setActionMessage(null);
//               }}
//               disabled={isSubmitting}
//             />
//             <span>
//               I, <strong>{tenantName}</strong>, have read and agree to the terms of the Rent Agreement above.
//             </span>
//           </label>

//           {/* Action Buttons */}
//           <div className="agreement-view-actions">
//             <button
//               className="agreement-view-btn agreement-view-btn-reject"
//               onClick={handleRejectClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✕ Reject'
//               )}
//             </button>
//             <button
//               className="agreement-view-btn agreement-view-btn-approve"
//               onClick={handleApproveClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✓ Approve'
//               )}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgreementView;

// // src/components/AgreementView.jsx

// import React, { useState, useEffect, useRef } from 'react';
// import { useParams, useSearchParams } from 'react-router-dom';
// import { 
//   sendAgreementLinkByQuery,
//   sendMsg91Otp,
//   verifyMsg91Otp,
//   generateOtpmsg91,
//   acceptRentAgreement,
//   rejectRentAgreement
// } from '../apis/services/pgApi';
// import ViewAgreementModal from './RentAgreementDynamic';
// import '../scss/components/_agreementview.scss';

// const AgreementView = () => {
//   const { agreementId } = useParams();
//   const [searchParams] = useSearchParams();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [agreementData, setAgreementData] = useState(null);
//   const [showModal, setShowModal] = useState(false);
  
//   // Agreement consent states
//   const [agreementConsent, setAgreementConsent] = useState(false);
  
//   // OTP related states
//   const [showOtpModal, setShowOtpModal] = useState(false);
//   const [otp, setOtp] = useState('');
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpId, setOtpId] = useState(null);
//   const [otpPurpose, setOtpPurpose] = useState(''); // 'approve' or 'reject'
//   const [otpMobile, setOtpMobile] = useState('');
//   const [otpError, setOtpError] = useState('');
//   const [otpSuccess, setOtpSuccess] = useState('');
//   const [isSendingOtp, setIsSendingOtp] = useState(false);
//   const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
//   const [resendTimer, setResendTimer] = useState(0);
//   const [otpVerified, setOtpVerified] = useState(false);
  
//   // For reject reason
//   const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
//   const [rejectReason, setRejectReason] = useState('');
//   const [rejectError, setRejectError] = useState('');

//   // Action states
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [actionMessage, setActionMessage] = useState(null);
//   const [actionSuccess, setActionSuccess] = useState(false);

//   const timerRef = useRef(null);

//   // Get tenantId and pgId from URL params
//   const tenantId = searchParams.get('tenant');
//   const pgId = searchParams.get('pg');

//   // Get the tenant name from agreement data
//   const getTenantName = () => {
//     if (!agreementData) return 'Tenant';
//     const data = agreementData.agreementjson || agreementData;
//     return data?.tenant?.name || data?.tenant?.Name || 'Tenant';
//   };

//   // Get tenant mobile from agreement data
//   const getTenantMobile = () => {
//     if (!agreementData) return '';
//     const data = agreementData.agreementjson || agreementData;
//     return data?.tenant?.mobile || data?.tenant?.Mobile || '';
//   };

//   // Get agreement ID
//   const getAgreementID = () => {
//     return agreementData?.AgreementID || agreementId;
//   };

//   useEffect(() => {
//     const fetchAgreementData = async () => {
//       setLoading(true);
//       setError(null);
//       setActionMessage(null);

//       try {
//         const finalPgId = pgId || 8746;
//         const finalTenantId = tenantId || 1025;

//         console.log('Fetching agreement data for PG:', finalPgId, 'Tenant:', finalTenantId);

//         const response = await sendAgreementLinkByQuery(finalPgId, finalTenantId);
        
//         console.log('Agreement response:', response);

//         if (response.success && response.data) {
//           let parsedData = response.data;
//           if (typeof response.data.agreementjson === 'string') {
//             try {
//               const jsonData = JSON.parse(response.data.agreementjson);
//               parsedData = {
//                 ...response.data,
//                 agreementjson: Array.isArray(jsonData) ? jsonData[0] : jsonData
//               };
//             } catch (parseError) {
//               console.error('Error parsing agreement JSON:', parseError);
//               parsedData.agreementjson = response.data.agreementjson;
//             }
//           } else if (Array.isArray(response.data.agreementjson)) {
//             parsedData = {
//               ...response.data,
//               agreementjson: response.data.agreementjson[0]
//             };
//           }

//           setAgreementData(parsedData);
//           setShowModal(true);
          
//           // Set mobile number from tenant data
//           const mobile = getTenantMobileFromData(parsedData);
//           console.log("mobile",mobile)
//           if (mobile) {
//             setOtpMobile(mobile);
//           }
//         } else {
//           setError(response.message || 'Failed to fetch agreement data');
//         }
//       } catch (err) {
//         console.error('Error fetching agreement:', err);
//         setError(err.message || 'An error occurred while fetching agreement');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAgreementData();
//   }, [agreementId, tenantId, pgId]);

//   const getTenantMobileFromData = (data) => {
//     const agreement = data.agreementjson || data;
//     return agreement?.tenant?.mobile || agreement?.tenant?.Mobile || '';
//   };

//   // Timer for resend OTP
//   useEffect(() => {
//     if (resendTimer > 0) {
//       timerRef.current = setTimeout(() => {
//         setResendTimer(resendTimer - 1);
//       }, 1000);
//     }
//     return () => {
//       if (timerRef.current) {
//         clearTimeout(timerRef.current);
//       }
//     };
//   }, [resendTimer]);

//   const handleCloseModal = () => {
//     setShowModal(false);
//   };

//   // Handle sending OTP - Only sends MSG91 OTP, does NOT generate in DB
//   const handleSendOtp = async (purpose) => {
//     // Validate consent
//     if (!agreementConsent) {
//       setActionMessage({
//         type: 'error',
//         text: 'Please accept the terms and conditions before proceeding.'
//       });
//       return;
//     }

//     // Validate mobile
//     if (!otpMobile || otpMobile.length < 10) {
//       setOtpError('Invalid mobile number. Please contact support.');
//       return;
//     }

//     setOtpPurpose(purpose);
//     setIsSendingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtpVerified(false);
//     setOtp('');
//     setOtpId(null);

//     try {
//       // Step 1: Send OTP via MSG91 only - No DB generation yet
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: otpMobile,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       console.log('Send OTP Response:', sendOtpResponse);

//       if (sendOtpResponse.success) {
//         setOtpSent(true);
//         setOtpSuccess('OTP sent successfully to your mobile number!');
//         setResendTimer(30); // 30 seconds cooldown
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to send OTP');
//       }
//     } catch (err) {
//       console.error('Error sending OTP:', err);
//       setOtpError(err.message || 'An error occurred while sending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Handle verifying OTP
//   const handleVerifyOtp = async () => {
//     // Validate OTP length (4 digits)
//     if (!otp || otp.length !== 4) {
//       setOtpError('Please enter a valid 4-digit OTP');
//       return;
//     }

//     setIsVerifyingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');

//     try {
//       // Step 1: Verify OTP via MSG91
//       const verifyResponse = await verifyMsg91Otp(otpMobile, otp);
      
//       console.log('Verify OTP Response:', verifyResponse);

//       if (verifyResponse.success) {
//         // Step 2: ONLY AFTER successful verification, generate OTP in database
//         // with the OTP that the user entered and was verified
//         const generateResponse = await generateOtpmsg91(
//           otpMobile,
//           'AGREEMENT_VERIFICATION',
//           otp, // The OTP that user entered and was verified
//           pgId || 8746,
//           getAgreementID()
//         );

//         console.log('Generate OTP in DB Response:', generateResponse);

//         if (generateResponse.success) {
//           setOtpId(generateResponse.data?.otpId);
//           setOtpVerified(true);
//           setOtpSuccess('OTP verified successfully!');
          
//           // Close OTP modal after success
//           setTimeout(() => {
//             setShowOtpModal(false);
//             // Execute the approve or reject action
//             if (otpPurpose === 'approve') {
//               executeApproveAgreement();
//             } else if (otpPurpose === 'reject') {
//               setShowRejectReasonModal(true);
//             }
//           }, 1000);
//         } else {
//           setOtpError(generateResponse.message || 'Failed to save OTP verification');
//         }
//       } else {
//         setOtpError(verifyResponse.message || 'Invalid OTP. Please try again.');
//       }
//     } catch (err) {
//       console.error('Error verifying OTP:', err);
//       setOtpError(err.message || 'An error occurred while verifying OTP');
//     } finally {
//       setIsVerifyingOtp(false);
//     }
//   };

//   // Handle resend OTP
//   const handleResendOtp = async () => {
//     if (resendTimer > 0) return;
    
//     setOtpError('');
//     setOtpSuccess('');
//     setIsSendingOtp(true);

//     try {
//       // Only resend MSG91 OTP - DB generation will happen after verification
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: otpMobile,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       if (sendOtpResponse.success) {
//         setOtpSuccess('OTP resent successfully!');
//         setResendTimer(30);
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to resend OTP');
//       }
//     } catch (err) {
//       console.error('Error resending OTP:', err);
//       setOtpError(err.message || 'An error occurred while resending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Execute Approve Agreement
//   const executeApproveAgreement = async () => {
//     setIsSubmitting(true);
//     setActionMessage(null);

//     try {
//       const agreementID = getAgreementID();
      
//       const acceptData = {
//         AgreementID: parseInt(agreementID),
//         OTPID: parseInt(otpId),
//         IPAddress: '192.168.1.100', // Hardcoded for now
//         DeviceInfo: navigator.userAgent || 'Chrome Browser',
//         Latitude: 28.613939, // Hardcoded for now
//         Longitude: 77.209021 // Hardcoded for now
//       };

//       console.log('Accepting agreement with data:', acceptData);

//       const response = await acceptRentAgreement(acceptData);

//       console.log('Accept Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(true);
//         setActionMessage({
//           type: 'success',
//           text: '🎉 Agreement accepted successfully! You can now download the signed agreement.'
//         });
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to accept agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error accepting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while accepting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Execute Reject Agreement
//   const executeRejectAgreement = async () => {
//     if (!rejectReason || rejectReason.trim().length === 0) {
//       setRejectError('Please provide a reason for rejection');
//       return;
//     }

//     setIsSubmitting(true);
//     setActionMessage(null);
//     setRejectError('');

//     try {
//       const agreementID = getAgreementID();
      
//       const rejectData = {
//         AgreementID: parseInt(agreementID),
//         RejectReason: rejectReason.trim(),
//         IPAddress: '192.168.1.100', // Hardcoded for now
//         DeviceInfo: navigator.userAgent || 'Chrome Browser'
//       };

//       console.log('Rejecting agreement with data:', rejectData);

//       const response = await rejectRentAgreement(rejectData);

//       console.log('Reject Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(false);
//         setActionMessage({
//           type: 'error',
//           text: '❌ Agreement rejected. You can contact the landlord for further discussion.'
//         });
//         setShowRejectReasonModal(false);
//         // Close modal after showing message
//         setTimeout(() => handleCloseModal(), 3000);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to reject agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error rejecting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while rejecting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Handle Approve button click - Show OTP modal
//   const handleApproveClick = () => {
//     setShowOtpModal(true);
//     setOtpPurpose('approve');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//     // Auto send OTP when modal opens
//     setTimeout(() => {
//       handleSendOtp('approve');
//     }, 500);
//   };

//   // Handle Reject button click - Show OTP modal first
//   const handleRejectClick = () => {
//     setShowOtpModal(true);
//     setOtpPurpose('reject');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//     // Auto send OTP when modal opens
//     setTimeout(() => {
//       handleSendOtp('reject');
//     }, 500);
//   };

//   // Close OTP modal
//   const handleCloseOtpModal = () => {
//     setShowOtpModal(false);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpId(null);
//   };

//   if (loading) {
//     return (
//       <div className="agreement-view-loading">
//         <div className="agreement-view-spinner"></div>
//         <p>Loading your agreement...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="agreement-view-error">
//         <div className="agreement-view-error-content">
//           <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
//             <circle cx="12" cy="12" r="10" />
//             <line x1="12" y1="8" x2="12" y2="12" />
//             <line x1="12" y1="16" x2="12.01" y2="16" />
//           </svg>
//           <h2>Unable to Load Agreement</h2>
//           <p>{error}</p>
//           <button 
//             className="agreement-view-retry-btn"
//             onClick={() => window.location.reload()}
//           >
//             Retry
//           </button>
//           <button 
//             className="agreement-view-home-btn"
//             onClick={() => window.location.href = '/'}
//           >
//             Go Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const tenantName = getTenantName();

//   return (
//     <div className="agreement-view-container">
//       {/* Success/Error Message */}
//       {actionMessage && (
//         <div className={`agreement-view-message ${actionMessage.type === 'success' ? 'agreement-view-message-success' : 'agreement-view-message-error'}`}>
//           {actionMessage.text}
//           {actionMessage.type === 'success' && actionSuccess && (
//             <button 
//               className="agreement-view-message-close"
//               onClick={() => setActionMessage(null)}
//             >
//               ×
//             </button>
//           )}
//         </div>
//       )}

//       {/* Agreement Modal */}
//       {showModal && agreementData && (
//         <ViewAgreementModal
//           data={agreementData.agreementjson || agreementData}
//           fileName={`Agreement_${agreementData.AgreementNo || 'Rent'}.pdf`}
//           onClose={handleCloseModal}
//         />
//       )}

//       {/* OTP Modal */}
//       {showOtpModal && (
//         <div className="agreement-otp-overlay">
//           <div className="agreement-otp-modal">
//             <div className="agreement-otp-header">
//               <h3>Verify Your Identity</h3>
//               <button className="agreement-otp-close" onClick={handleCloseOtpModal}>
//                 ×
//               </button>
//             </div>
//             <div className="agreement-otp-body">
//               <p className="agreement-otp-info">
//                 We've sent a 4-digit verification code to <strong>{otpMobile}</strong>
//               </p>
//               <p className="agreement-otp-purpose">
//                 Purpose: {otpPurpose === 'approve' ? 'Approve Agreement' : 'Reject Agreement'}
//               </p>

//               {otpError && (
//                 <div className="agreement-otp-error">{otpError}</div>
//               )}
//               {otpSuccess && (
//                 <div className="agreement-otp-success">{otpSuccess}</div>
//               )}

//               <div className="agreement-otp-input-group">
//                 <input
//                   type="text"
//                   className="agreement-otp-input"
//                   placeholder="Enter 4-digit OTP"
//                   value={otp}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, '');
//                     if (value.length <= 4) {
//                       setOtp(value);
//                     }
//                   }}
//                   maxLength="4"
//                   disabled={isVerifyingOtp || otpVerified}
//                   autoFocus
//                 />
//               </div>

//               <div className="agreement-otp-actions">
//                 <button
//                   className="agreement-otp-btn agreement-otp-btn-verify"
//                   onClick={handleVerifyOtp}
//                   disabled={isVerifyingOtp || otpVerified || otp.length !== 4}
//                 >
//                   {isVerifyingOtp ? 'Verifying...' : otpVerified ? '✓ Verified' : 'Verify OTP'}
//                 </button>
//               </div>

//               <div className="agreement-otp-resend">
//                 <button
//                   className="agreement-otp-btn-resend"
//                   onClick={handleResendOtp}
//                   disabled={isSendingOtp || resendTimer > 0 || otpVerified}
//                 >
//                   {isSendingOtp ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Reject Reason Modal */}
//       {showRejectReasonModal && (
//         <div className="agreement-reject-overlay">
//           <div className="agreement-reject-modal">
//             <div className="agreement-reject-header">
//               <h3>Reason for Rejection</h3>
//               <button 
//                 className="agreement-reject-close" 
//                 onClick={() => setShowRejectReasonModal(false)}
//               >
//                 ×
//               </button>
//             </div>
//             <div className="agreement-reject-body">
//               <p className="agreement-reject-info">
//                 Please provide a reason for rejecting this agreement:
//               </p>
//               {rejectError && (
//                 <div className="agreement-reject-error">{rejectError}</div>
//               )}
//               <textarea
//                 className="agreement-reject-textarea"
//                 placeholder="Enter your reason for rejection..."
//                 value={rejectReason}
//                 onChange={(e) => setRejectReason(e.target.value)}
//                 rows="4"
//                 disabled={isSubmitting}
//               />
//               <div className="agreement-reject-actions">
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-cancel"
//                   onClick={() => setShowRejectReasonModal(false)}
//                   disabled={isSubmitting}
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-submit"
//                   onClick={executeRejectAgreement}
//                   disabled={isSubmitting || !rejectReason.trim()}
//                 >
//                   {isSubmitting ? 'Submitting...' : 'Submit Rejection'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Footer with Checkbox and Buttons */}
//       <div className="agreement-view-footer">
//         <div className="agreement-view-footer-content">
//           {/* Checkbox */}
//           <label className="agreement-view-consent">
//             <input
//               type="checkbox"
//               checked={agreementConsent}
//               onChange={(e) => {
//                 setAgreementConsent(e.target.checked);
//                 if (actionMessage) setActionMessage(null);
//               }}
//               disabled={isSubmitting}
//             />
//             <span>
//               I, <strong>{tenantName}</strong>, have read and agree to the terms of the Rent Agreement above.
//             </span>
//           </label>

//           {/* Action Buttons */}
//           <div className="agreement-view-actions">
//             <button
//               className="agreement-view-btn agreement-view-btn-reject"
//               onClick={handleRejectClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✕ Reject'
//               )}
//             </button>
//             <button
//               className="agreement-view-btn agreement-view-btn-approve"
//               onClick={handleApproveClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✓ Approve'
//               )}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgreementView;

// // src/components/AgreementView.jsx

// import React, { useState, useEffect, useRef } from 'react';
// import { useParams, useSearchParams } from 'react-router-dom';
// import { 
//   sendAgreementLinkByQuery,
//   sendMsg91Otp,
//   verifyMsg91Otp,
//   generateOtpmsg91,
//   acceptRentAgreement,
//   rejectRentAgreement
// } from '../apis/services/pgApi';
// import ViewAgreementModal from './RentAgreementDynamic';
// import '../scss/components/_agreementview.scss';

// const AgreementView = () => {
//   const { agreementId } = useParams();
//   const [searchParams] = useSearchParams();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [agreementData, setAgreementData] = useState(null);
//   const [showModal, setShowModal] = useState(false);
  
//   // Agreement consent states
//   const [agreementConsent, setAgreementConsent] = useState(false);
  
//   // OTP related states
//   const [showOtpModal, setShowOtpModal] = useState(false);
//   const [otp, setOtp] = useState('');
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpId, setOtpId] = useState(null);
//   const [otpPurpose, setOtpPurpose] = useState(''); // 'approve' or 'reject'
//   const [otpMobile, setOtpMobile] = useState('');
//   const [otpError, setOtpError] = useState('');
//   const [otpSuccess, setOtpSuccess] = useState('');
//   const [isSendingOtp, setIsSendingOtp] = useState(false);
//   const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
//   const [resendTimer, setResendTimer] = useState(0);
//   const [otpVerified, setOtpVerified] = useState(false);
  
//   // For reject reason
//   const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
//   const [rejectReason, setRejectReason] = useState('');
//   const [rejectError, setRejectError] = useState('');

//   // Action states
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [actionMessage, setActionMessage] = useState(null);
//   const [actionSuccess, setActionSuccess] = useState(false);

//   const timerRef = useRef(null);

//   // Get tenantId and pgId from URL params
//   const tenantId = searchParams.get('tenant');
//   const pgId = searchParams.get('pg');

//   // Get the tenant name from agreement data
//   const getTenantName = () => {
//     if (!agreementData) return 'Tenant';
//     const data = agreementData.agreementjson || agreementData;
//     return data?.tenant?.name || data?.tenant?.Name || 'Tenant';
//   };

//   // Get tenant mobile from agreement data
//   const getTenantMobile = () => {
//     if (!agreementData) return '';
//     const data = agreementData.agreementjson || agreementData;
//     return data?.tenant?.mobile || data?.tenant?.Mobile || '';
//   };

//   // Get agreement ID
//   const getAgreementID = () => {
//     return agreementData?.AgreementID || agreementId;
//   };

//   useEffect(() => {
//     const fetchAgreementData = async () => {
//       setLoading(true);
//       setError(null);
//       setActionMessage(null);

//       try {
//         const finalPgId = pgId || 8746;
//         const finalTenantId = tenantId || 1025;

//         console.log('Fetching agreement data for PG:', finalPgId, 'Tenant:', finalTenantId);

//         const response = await sendAgreementLinkByQuery(finalPgId, finalTenantId);
        
//         console.log('Agreement response:', response);

//         if (response.success && response.data) {
//           let parsedData = response.data;
//           if (typeof response.data.agreementjson === 'string') {
//             try {
//               const jsonData = JSON.parse(response.data.agreementjson);
//               parsedData = {
//                 ...response.data,
//                 agreementjson: Array.isArray(jsonData) ? jsonData[0] : jsonData
//               };
//             } catch (parseError) {
//               console.error('Error parsing agreement JSON:', parseError);
//               parsedData.agreementjson = response.data.agreementjson;
//             }
//           } else if (Array.isArray(response.data.agreementjson)) {
//             parsedData = {
//               ...response.data,
//               agreementjson: response.data.agreementjson[0]
//             };
//           }

//           setAgreementData(parsedData);
//           setShowModal(true);
          
//           // Set mobile number from tenant data
//           const mobile = getTenantMobileFromData(parsedData);
//           if (mobile) {
//             setOtpMobile(mobile);
//           }
//         } else {
//           setError(response.message || 'Failed to fetch agreement data');
//         }
//       } catch (err) {
//         console.error('Error fetching agreement:', err);
//         setError(err.message || 'An error occurred while fetching agreement');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAgreementData();
//   }, [agreementId, tenantId, pgId]);

//   const getTenantMobileFromData = (data) => {
//     const agreement = data.agreementjson || data;
//     return agreement?.tenant?.mobile || agreement?.tenant?.Mobile || '';
//   };

//   // Timer for resend OTP
//   useEffect(() => {
//     if (resendTimer > 0) {
//       timerRef.current = setTimeout(() => {
//         setResendTimer(resendTimer - 1);
//       }, 1000);
//     }
//     return () => {
//       if (timerRef.current) {
//         clearTimeout(timerRef.current);
//       }
//     };
//   }, [resendTimer]);

//   const handleCloseModal = () => {
//     setShowModal(false);
//   };

//   // Handle sending OTP
//   const handleSendOtp = async (purpose) => {
//     // Validate consent
//     if (!agreementConsent) {
//       setActionMessage({
//         type: 'error',
//         text: 'Please accept the terms and conditions before proceeding.'
//       });
//       return;
//     }

//     // Validate mobile
//     if (!otpMobile || otpMobile.length < 10) {
//       setOtpError('Invalid mobile number. Please contact support.');
//       return;
//     }

//     setOtpPurpose(purpose);
//     setIsSendingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtpVerified(false);
//     setOtp('');

//     try {
//       // Step 1: Send OTP via MSG91
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: otpMobile,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       console.log('Send OTP Response:', sendOtpResponse);

//       if (sendOtpResponse.success) {
//         setOtpSent(true);
//         setOtpSuccess('OTP sent successfully to your mobile number!');
//         setResendTimer(30); // 30 seconds cooldown
        
//         // Step 2: Generate OTP in database (hardcoded OTP for demo - in production use actual OTP from MSG91)
//         // For demo purposes, we're using a fixed OTP. In production, you should get the actual OTP from MSG91
//         const generatedOtp = '123456'; // For demo only
        
//         const generateResponse = await generateOtpmsg91(
//           otpMobile,
//           'AGREEMENT_VERIFICATION',
//           generatedOtp,
//           pgId || 8746,
//           getAgreementID()
//         );

//         console.log('Generate OTP Response:', generateResponse);

//         if (generateResponse.success) {
//           setOtpId(generateResponse.data?.otpId);
//           setOtpSuccess('OTP sent successfully! Please check your mobile.');
//         } else {
//           setOtpError(generateResponse.message || 'Failed to generate OTP');
//         }
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to send OTP');
//       }
//     } catch (err) {
//       console.error('Error sending OTP:', err);
//       setOtpError(err.message || 'An error occurred while sending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Handle verifying OTP
//   const handleVerifyOtp = async () => {
//     if (!otp || otp.length !== 6) {
//       setOtpError('Please enter a valid 6-digit OTP');
//       return;
//     }

//     setIsVerifyingOtp(true);
//     setOtpError('');
//     setOtpSuccess('');

//     try {
//       // Step 1: Verify OTP via MSG91
//       const verifyResponse = await verifyMsg91Otp(otpMobile, otp);
      
//       console.log('Verify OTP Response:', verifyResponse);

//       if (verifyResponse.success) {
//         setOtpVerified(true);
//         setOtpSuccess('OTP verified successfully!');
        
//         // Close OTP modal after success
//         setTimeout(() => {
//           setShowOtpModal(false);
//           // Execute the approve or reject action
//           if (otpPurpose === 'approve') {
//             executeApproveAgreement();
//           } else if (otpPurpose === 'reject') {
//             setShowRejectReasonModal(true);
//           }
//         }, 1000);
//       } else {
//         setOtpError(verifyResponse.message || 'Invalid OTP. Please try again.');
//       }
//     } catch (err) {
//       console.error('Error verifying OTP:', err);
//       setOtpError(err.message || 'An error occurred while verifying OTP');
//     } finally {
//       setIsVerifyingOtp(false);
//     }
//   };

//   // Handle resend OTP
//   const handleResendOtp = async () => {
//     if (resendTimer > 0) return;
    
//     setOtpError('');
//     setOtpSuccess('');
//     setIsSendingOtp(true);

//     try {
//       const sendOtpResponse = await sendMsg91Otp({
//         mobile: otpMobile,
//         param1: 'OTP',
//         param2: 'Agreement Verification'
//       });

//       if (sendOtpResponse.success) {
//         setOtpSuccess('OTP resent successfully!');
//         setResendTimer(30);
        
//         // Regenerate OTP in database
//         const generatedOtp = '123456'; // For demo only
//         await generateOtpmsg91(
//           otpMobile,
//           'AGREEMENT_VERIFICATION',
//           generatedOtp,
//           pgId || 8746,
//           getAgreementID()
//         );
//       } else {
//         setOtpError(sendOtpResponse.message || 'Failed to resend OTP');
//       }
//     } catch (err) {
//       console.error('Error resending OTP:', err);
//       setOtpError(err.message || 'An error occurred while resending OTP');
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   // Execute Approve Agreement
//   const executeApproveAgreement = async () => {
//     setIsSubmitting(true);
//     setActionMessage(null);

//     try {
//       const agreementID = getAgreementID();
      
//       const acceptData = {
//         AgreementID: parseInt(agreementID),
//         OTPID: parseInt(otpId),
//         IPAddress: '192.168.1.100', // Hardcoded for now
//         DeviceInfo: navigator.userAgent || 'Chrome Browser',
//         Latitude: 28.613939, // Hardcoded for now
//         Longitude: 77.209021 // Hardcoded for now
//       };

//       console.log('Accepting agreement with data:', acceptData);

//       const response = await acceptRentAgreement(acceptData);

//       console.log('Accept Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(true);
//         setActionMessage({
//           type: 'success',
//           text: '🎉 Agreement accepted successfully! You can now download the signed agreement.'
//         });
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to accept agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error accepting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while accepting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Execute Reject Agreement
//   const executeRejectAgreement = async () => {
//     if (!rejectReason || rejectReason.trim().length === 0) {
//       setRejectError('Please provide a reason for rejection');
//       return;
//     }

//     setIsSubmitting(true);
//     setActionMessage(null);
//     setRejectError('');

//     try {
//       const agreementID = getAgreementID();
      
//       const rejectData = {
//         AgreementID: parseInt(agreementID),
//         RejectReason: rejectReason.trim(),
//         IPAddress: '192.168.1.100', // Hardcoded for now
//         DeviceInfo: navigator.userAgent || 'Chrome Browser'
//       };

//       console.log('Rejecting agreement with data:', rejectData);

//       const response = await rejectRentAgreement(rejectData);

//       console.log('Reject Agreement Response:', response);

//       if (response.success) {
//         setActionSuccess(false);
//         setActionMessage({
//           type: 'error',
//           text: '❌ Agreement rejected. You can contact the landlord for further discussion.'
//         });
//         setShowRejectReasonModal(false);
//         // Close modal after showing message
//         setTimeout(() => handleCloseModal(), 3000);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: response.message || 'Failed to reject agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error rejecting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while rejecting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Handle Approve button click - Show OTP modal
//   const handleApproveClick = () => {
//     setShowOtpModal(true);
//     setOtpPurpose('approve');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     // Auto send OTP when modal opens
//     setTimeout(() => {
//       handleSendOtp('approve');
//     }, 500);
//   };

//   // Handle Reject button click - Show OTP modal first
//   const handleRejectClick = () => {
//     setShowOtpModal(true);
//     setOtpPurpose('reject');
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//     // Auto send OTP when modal opens
//     setTimeout(() => {
//       handleSendOtp('reject');
//     }, 500);
//   };

//   // Close OTP modal
//   const handleCloseOtpModal = () => {
//     setShowOtpModal(false);
//     setOtpError('');
//     setOtpSuccess('');
//     setOtp('');
//     setOtpSent(false);
//     setOtpVerified(false);
//   };

//   if (loading) {
//     return (
//       <div className="agreement-view-loading">
//         <div className="agreement-view-spinner"></div>
//         <p>Loading your agreement...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="agreement-view-error">
//         <div className="agreement-view-error-content">
//           <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
//             <circle cx="12" cy="12" r="10" />
//             <line x1="12" y1="8" x2="12" y2="12" />
//             <line x1="12" y1="16" x2="12.01" y2="16" />
//           </svg>
//           <h2>Unable to Load Agreement</h2>
//           <p>{error}</p>
//           <button 
//             className="agreement-view-retry-btn"
//             onClick={() => window.location.reload()}
//           >
//             Retry
//           </button>
//           <button 
//             className="agreement-view-home-btn"
//             onClick={() => window.location.href = '/'}
//           >
//             Go Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const tenantName = getTenantName();

//   return (
//     <div className="agreement-view-container">
//       {/* Success/Error Message */}
//       {actionMessage && (
//         <div className={`agreement-view-message ${actionMessage.type === 'success' ? 'agreement-view-message-success' : 'agreement-view-message-error'}`}>
//           {actionMessage.text}
//           {actionMessage.type === 'success' && actionSuccess && (
//             <button 
//               className="agreement-view-message-close"
//               onClick={() => setActionMessage(null)}
//             >
//               ×
//             </button>
//           )}
//         </div>
//       )}

//       {/* Agreement Modal */}
//       {showModal && agreementData && (
//         <ViewAgreementModal
//           data={agreementData.agreementjson || agreementData}
//           fileName={`Agreement_${agreementData.AgreementNo || 'Rent'}.pdf`}
//           onClose={handleCloseModal}
//         />
//       )}

//       {/* OTP Modal */}
//       {showOtpModal && (
//         <div className="agreement-otp-overlay">
//           <div className="agreement-otp-modal">
//             <div className="agreement-otp-header">
//               <h3>Verify Your Identity</h3>
//               <button className="agreement-otp-close" onClick={handleCloseOtpModal}>
//                 ×
//               </button>
//             </div>
//             <div className="agreement-otp-body">
//               <p className="agreement-otp-info">
//                 We've sent a verification code to <strong>{otpMobile}</strong>
//               </p>
//               <p className="agreement-otp-purpose">
//                 Purpose: {otpPurpose === 'approve' ? 'Approve Agreement' : 'Reject Agreement'}
//               </p>

//               {otpError && (
//                 <div className="agreement-otp-error">{otpError}</div>
//               )}
//               {otpSuccess && (
//                 <div className="agreement-otp-success">{otpSuccess}</div>
//               )}

//               <div className="agreement-otp-input-group">
//                 <input
//                   type="text"
//                   className="agreement-otp-input"
//                   placeholder="Enter 6-digit OTP"
//                   value={otp}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, '');
//                     if (value.length <= 6) {
//                       setOtp(value);
//                     }
//                   }}
//                   maxLength="6"
//                   disabled={isVerifyingOtp || otpVerified}
//                   autoFocus
//                 />
//               </div>

//               <div className="agreement-otp-actions">
//                 <button
//                   className="agreement-otp-btn agreement-otp-btn-verify"
//                   onClick={handleVerifyOtp}
//                   disabled={isVerifyingOtp || otpVerified || otp.length !== 6}
//                 >
//                   {isVerifyingOtp ? 'Verifying...' : otpVerified ? '✓ Verified' : 'Verify OTP'}
//                 </button>
//               </div>

//               <div className="agreement-otp-resend">
//                 <button
//                   className="agreement-otp-btn-resend"
//                   onClick={handleResendOtp}
//                   disabled={isSendingOtp || resendTimer > 0 || otpVerified}
//                 >
//                   {isSendingOtp ? 'Sending...' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Reject Reason Modal */}
//       {showRejectReasonModal && (
//         <div className="agreement-reject-overlay">
//           <div className="agreement-reject-modal">
//             <div className="agreement-reject-header">
//               <h3>Reason for Rejection</h3>
//               <button 
//                 className="agreement-reject-close" 
//                 onClick={() => setShowRejectReasonModal(false)}
//               >
//                 ×
//               </button>
//             </div>
//             <div className="agreement-reject-body">
//               <p className="agreement-reject-info">
//                 Please provide a reason for rejecting this agreement:
//               </p>
//               {rejectError && (
//                 <div className="agreement-reject-error">{rejectError}</div>
//               )}
//               <textarea
//                 className="agreement-reject-textarea"
//                 placeholder="Enter your reason for rejection..."
//                 value={rejectReason}
//                 onChange={(e) => setRejectReason(e.target.value)}
//                 rows="4"
//                 disabled={isSubmitting}
//               />
//               <div className="agreement-reject-actions">
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-cancel"
//                   onClick={() => setShowRejectReasonModal(false)}
//                   disabled={isSubmitting}
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   className="agreement-reject-btn agreement-reject-btn-submit"
//                   onClick={executeRejectAgreement}
//                   disabled={isSubmitting || !rejectReason.trim()}
//                 >
//                   {isSubmitting ? 'Submitting...' : 'Submit Rejection'}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Footer with Checkbox and Buttons */}
//       <div className="agreement-view-footer">
//         <div className="agreement-view-footer-content">
//           {/* Checkbox */}
//           <label className="agreement-view-consent">
//             <input
//               type="checkbox"
//               checked={agreementConsent}
//               onChange={(e) => {
//                 setAgreementConsent(e.target.checked);
//                 if (actionMessage) setActionMessage(null);
//               }}
//               disabled={isSubmitting}
//             />
//             <span>
//               I, <strong>{tenantName}</strong>, have read and agree to the terms of the Rent Agreement above.
//             </span>
//           </label>

//           {/* Action Buttons */}
//           <div className="agreement-view-actions">
//             <button
//               className="agreement-view-btn agreement-view-btn-reject"
//               onClick={handleRejectClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✕ Reject'
//               )}
//             </button>
//             <button
//               className="agreement-view-btn agreement-view-btn-approve"
//               onClick={handleApproveClick}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✓ Approve'
//               )}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgreementView;




// // src/components/AgreementView.jsx

// import React, { useState, useEffect } from 'react';
// import { useParams, useSearchParams } from 'react-router-dom';
// import { sendAgreementLinkByQuery } from '../apis/services/pgApi';
// import ViewAgreementModal from './RentAgreementDynamic';
// import '../scss/components/_agreementview.scss';

// const AgreementView = () => {
//   const { agreementId } = useParams();
//   const [searchParams] = useSearchParams();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [agreementData, setAgreementData] = useState(null);
//   const [showModal, setShowModal] = useState(false);
  
//   // New states for agreement actions
//   const [agreementConsent, setAgreementConsent] = useState(false);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [actionMessage, setActionMessage] = useState(null);
//   const [actionSuccess, setActionSuccess] = useState(false);

//   // Get tenantId and pgId from URL params
//   const tenantId = searchParams.get('tenant');
//   const pgId = searchParams.get('pg');

//   // Get the tenant name from agreement data
//   const getTenantName = () => {
//     if (!agreementData) return 'Tenant';
//     const data = agreementData.agreementjson || agreementData;
//     return data?.tenant?.name || data?.tenant?.Name || 'Tenant';
//   };

//   useEffect(() => {
//     const fetchAgreementData = async () => {
//       setLoading(true);
//       setError(null);
//       setActionMessage(null);

//       try {
//         // Use provided IDs or fallback to defaults
//         const finalPgId = pgId || 8746;
//         const finalTenantId = tenantId || 1025;

//         console.log('Fetching agreement data for PG:', finalPgId, 'Tenant:', finalTenantId);

//         const response = await sendAgreementLinkByQuery(finalPgId, finalTenantId);
        
//         console.log('Agreement response:', response);

//         if (response.success && response.data) {
//           // Parse agreementjson if it's a string
//           let parsedData = response.data;
//           if (typeof response.data.agreementjson === 'string') {
//             try {
//               const jsonData = JSON.parse(response.data.agreementjson);
//               parsedData = {
//                 ...response.data,
//                 agreementjson: Array.isArray(jsonData) ? jsonData[0] : jsonData
//               };
//             } catch (parseError) {
//               console.error('Error parsing agreement JSON:', parseError);
//               parsedData.agreementjson = response.data.agreementjson;
//             }
//           } else if (Array.isArray(response.data.agreementjson)) {
//             parsedData = {
//               ...response.data,
//               agreementjson: response.data.agreementjson[0]
//             };
//           }

//           setAgreementData(parsedData);
//           setShowModal(true);
//         } else {
//           setError(response.message || 'Failed to fetch agreement data');
//         }
//       } catch (err) {
//         console.error('Error fetching agreement:', err);
//         setError(err.message || 'An error occurred while fetching agreement');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAgreementData();
//   }, [agreementId, tenantId, pgId]);

//   const handleCloseModal = () => {
//     setShowModal(false);
//     // Optionally redirect or close the view
//   };

//   // Handle Approve Agreement
//   const handleApproveAgreement = async () => {
//     if (!agreementConsent) {
//       setActionMessage({
//         type: 'error',
//         text: 'Please accept the terms and conditions before approving.'
//       });
//       return;
//     }

//     setIsSubmitting(true);
//     setActionMessage(null);

//     try {
//       // Get the agreement ID from the data
//       const agreementID = agreementData?.AgreementID || agreementId;
      
//       // TODO: Call your accept agreement API here
//       // const response = await acceptRentAgreement({
//       //   AgreementID: parseInt(agreementID),
//       //   OTPID: 0, // You may need OTP verification
//       //   IPAddress: '192.168.1.100',
//       //   DeviceInfo: navigator.userAgent
//       // });

//       // Simulate API call
//       await new Promise(resolve => setTimeout(resolve, 1500));

//       // For demo purposes - remove this when API is integrated
//       const mockResponse = {
//         success: true,
//         message: 'Agreement accepted successfully'
//       };

//       if (mockResponse.success) {
//         setActionSuccess(true);
//         setActionMessage({
//           type: 'success',
//           text: '🎉 Agreement accepted successfully! You can now download the signed agreement.'
//         });
//         // You can also close the modal after success
//         // setTimeout(() => handleCloseModal(), 3000);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: mockResponse.message || 'Failed to accept agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error accepting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while accepting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Handle Reject Agreement
//   const handleRejectAgreement = async () => {
//     if (!agreementConsent) {
//       setActionMessage({
//         type: 'error',
//         text: 'Please accept the terms and conditions before rejecting.'
//       });
//       return;
//     }

//     setIsSubmitting(true);
//     setActionMessage(null);

//     try {
//       // Get the agreement ID from the data
//       const agreementID = agreementData?.AgreementID || agreementId;
      
//       // TODO: Call your reject agreement API here
//       // const response = await rejectRentAgreement({
//       //   AgreementID: parseInt(agreementID),
//       //   RejectReason: 'Tenant rejected the agreement',
//       //   IPAddress: '192.168.1.100',
//       //   DeviceInfo: navigator.userAgent
//       // });

//       // Simulate API call
//       await new Promise(resolve => setTimeout(resolve, 1500));

//       // For demo purposes - remove this when API is integrated
//       const mockResponse = {
//         success: true,
//         message: 'Agreement rejected successfully'
//       };

//       if (mockResponse.success) {
//         setActionSuccess(false);
//         setActionMessage({
//           type: 'error',
//           text: '❌ Agreement rejected. You can contact the landlord for further discussion.'
//         });
//         // Close modal after showing message
//         setTimeout(() => handleCloseModal(), 3000);
//       } else {
//         setActionMessage({
//           type: 'error',
//           text: mockResponse.message || 'Failed to reject agreement'
//         });
//       }
//     } catch (err) {
//       console.error('Error rejecting agreement:', err);
//       setActionMessage({
//         type: 'error',
//         text: err.message || 'An error occurred while rejecting the agreement'
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="agreement-view-loading">
//         <div className="agreement-view-spinner"></div>
//         <p>Loading your agreement...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="agreement-view-error">
//         <div className="agreement-view-error-content">
//           <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
//             <circle cx="12" cy="12" r="10" />
//             <line x1="12" y1="8" x2="12" y2="12" />
//             <line x1="12" y1="16" x2="12.01" y2="16" />
//           </svg>
//           <h2>Unable to Load Agreement</h2>
//           <p>{error}</p>
//           <button 
//             className="agreement-view-retry-btn"
//             onClick={() => window.location.reload()}
//           >
//             Retry
//           </button>
//           <button 
//             className="agreement-view-home-btn"
//             onClick={() => window.location.href = '/'}
//           >
//             Go Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const tenantName = getTenantName();

//   return (
//     <div className="agreement-view-container">
//       {/* Success/Error Message */}
//       {actionMessage && (
//         <div className={`agreement-view-message ${actionMessage.type === 'success' ? 'agreement-view-message-success' : 'agreement-view-message-error'}`}>
//           {actionMessage.text}
//           {actionMessage.type === 'success' && actionSuccess && (
//             <button 
//               className="agreement-view-message-close"
//               onClick={() => setActionMessage(null)}
//             >
//               ×
//             </button>
//           )}
//         </div>
//       )}

//       {/* Agreement Modal */}
//       {showModal && agreementData && (
//         <ViewAgreementModal
//           data={agreementData.agreementjson || agreementData}
//           fileName={`Agreement_${agreementData.AgreementNo || 'Rent'}.pdf`}
//           onClose={handleCloseModal}
//         />
//       )}

//       {/* Footer with Checkbox and Buttons */}
//       <div className="agreement-view-footer">
//         <div className="agreement-view-footer-content">
//           {/* Checkbox */}
//           <label className="agreement-view-consent">
//             <input
//               type="checkbox"
//               checked={agreementConsent}
//               onChange={(e) => {
//                 setAgreementConsent(e.target.checked);
//                 if (actionMessage) setActionMessage(null);
//               }}
//               disabled={isSubmitting}
//             />
//             <span>
//               I, <strong>{tenantName}</strong>, have read and agree to the terms of the Rent Agreement above.
//             </span>
//           </label>

//           {/* Action Buttons */}
//           <div className="agreement-view-actions">
//             <button
//               className="agreement-view-btn agreement-view-btn-reject"
//               onClick={handleRejectAgreement}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✕ Reject'
//               )}
//             </button>
//             <button
//               className="agreement-view-btn agreement-view-btn-approve"
//               onClick={handleApproveAgreement}
//               disabled={isSubmitting || !agreementConsent}
//             >
//               {isSubmitting ? (
//                 <>
//                   <span className="agreement-view-spinner-small"></span>
//                   Processing...
//                 </>
//               ) : (
//                 '✓ Approve'
//               )}
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AgreementView;

// // src/components/AgreementView.jsx

// import React, { useState, useEffect } from 'react';
// import { useParams, useSearchParams } from 'react-router-dom';
// import { sendAgreementLinkByQuery } from '../apis/services/pgApi';
// import ViewAgreementModal from './RentAgreementDynamic';
// import '../scss/components/_agreementview.scss';

// const AgreementView = () => {
//   const { agreementId } = useParams();
//   const [searchParams] = useSearchParams();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [agreementData, setAgreementData] = useState(null);
//   const [showModal, setShowModal] = useState(false);

//   // Get tenantId and pgId from URL params
//   const tenantId = searchParams.get('tenant');
//   const pgId = searchParams.get('pg');

//   useEffect(() => {
//     const fetchAgreementData = async () => {
//       setLoading(true);
//       setError(null);

//       try {
//         // Use provided IDs or fallback to defaults
//         const finalPgId = pgId || 8746;
//         const finalTenantId = tenantId || 1025;

//         console.log('Fetching agreement data for PG:', finalPgId, 'Tenant:', finalTenantId);

//         const response = await sendAgreementLinkByQuery(finalPgId, finalTenantId);
        
//         console.log('Agreement response:', response);

//         if (response.success && response.data) {
//           // Parse agreementjson if it's a string
//           let parsedData = response.data;
//           if (typeof response.data.agreementjson === 'string') {
//             try {
//               const jsonData = JSON.parse(response.data.agreementjson);
//               parsedData = {
//                 ...response.data,
//                 agreementjson: Array.isArray(jsonData) ? jsonData[0] : jsonData
//               };
//             } catch (parseError) {
//               console.error('Error parsing agreement JSON:', parseError);
//               parsedData.agreementjson = response.data.agreementjson;
//             }
//           } else if (Array.isArray(response.data.agreementjson)) {
//             parsedData = {
//               ...response.data,
//               agreementjson: response.data.agreementjson[0]
//             };
//           }

//           setAgreementData(parsedData);
//           setShowModal(true);
//         } else {
//           setError(response.message || 'Failed to fetch agreement data');
//         }
//       } catch (err) {
//         console.error('Error fetching agreement:', err);
//         setError(err.message || 'An error occurred while fetching agreement');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchAgreementData();
//   }, [agreementId, tenantId, pgId]);

//   const handleCloseModal = () => {
//     setShowModal(false);
//     // Optionally redirect or close the view
//   };

//   if (loading) {
//     return (
//       <div className="agreement-view-loading">
//         <div className="agreement-view-spinner"></div>
//         <p>Loading your agreement...</p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="agreement-view-error">
//         <div className="agreement-view-error-content">
//           <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
//             <circle cx="12" cy="12" r="10" />
//             <line x1="12" y1="8" x2="12" y2="12" />
//             <line x1="12" y1="16" x2="12.01" y2="16" />
//           </svg>
//           <h2>Unable to Load Agreement</h2>
//           <p>{error}</p>
//           <button 
//             className="agreement-view-retry-btn"
//             onClick={() => window.location.reload()}
//           >
//             Retry
//           </button>
//           <button 
//             className="agreement-view-home-btn"
//             onClick={() => window.location.href = '/'}
//           >
//             Go Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="agreement-view-container">
//       {showModal && agreementData && (
//         <ViewAgreementModal
//           data={agreementData.agreementjson || agreementData}
//           fileName={`Agreement_${agreementData.AgreementNo || 'Rent'}.pdf`}
//           onClose={handleCloseModal}
//         />
//       )}
//     </div>
//   );
// };

// export default AgreementView;